<template>
    <div v-if="open" class="ai-shell" :class="'pv-' + previewMode">
        <button type="button" class="ai-scrim" :aria-label="$t('ai_offer.modal_minimize')" @click="close"></button>

        <transition name="ai-peek">
            <div v-if="previewOpen && previewMode === 'side' && !booting" class="ai-side-preview">
                <ai-offer-preview mode="side" v-bind="previewProps" @close="previewOpen = false" />
            </div>
        </transition>

        <div class="ai-modal" role="dialog" :aria-label="$t('ai_offer.modal_title')">
            <aside class="ai-rail">
                <div class="ai-rail-brand">
                    <span class="logo"><svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true"><path d="M9 1.5L10.6 6.4L15.5 8L10.6 9.6L9 14.5L7.4 9.6L2.5 8L7.4 6.4L9 1.5Z"></path><path d="M14.75 12.25L15.4 14.1L17.25 14.75L15.4 15.4L14.75 17.25L14.1 15.4L12.25 14.75L14.1 14.1L14.75 12.25Z"></path></svg></span>
                    <span>
                        <b>{{ $t("ai_offer.rail_title") }}</b>
                        <small>{{ $t("ai_offer.rail_sub") }}</small>
                    </span>
                </div>
                <div class="ai-steps">
                    <button
                        v-for="s in steps"
                        :key="s"
                        type="button"
                        class="ai-step"
                        :class="stepClass(s)"
                        :disabled="loading || !!result"
                        @click="goTo(s)"
                    >
                        <b>
                            <template v-if="isStepDone(s) && step !== s"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg></template>
                            <template v-else>{{ s }}</template>
                        </b>
                        <span class="t">
                            <span>{{ $t("ai_offer.step_" + s) }}</span>
                            <small>{{ $t("ai_offer.step_" + s + "_sub") }}</small>
                        </span>
                    </button>
                </div>
            </aside>

            <section class="ai-main">
                <div class="ai-modal-head">
                    <div v-if="loading || result" class="ai-head-bar"><i :style="{ width: genPct + '%' }"></i></div>
                    <div class="ai-head-text">
                        <div class="kicker">{{ headKicker }}</div>
                        <h2>{{ headTitle }}</h2>
                        <p v-if="headLead" class="lead">{{ headLead }}</p>
                    </div>
                    <div class="ai-head-actions">
                        <button
                            v-if="!booting && !loading && !result"
                            type="button"
                            class="ai-tog"
                            :class="{ on: previewOpen }"
                            @click="togglePreview"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                            <span>{{ $t("ai_offer.preview_btn") }}</span>
                        </button>
                        <button type="button" class="close" :aria-label="$t('ai_offer.modal_minimize')" @click="close">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"></line><line x1="18" y1="6" x2="6" y2="18"></line></svg>
                        </button>
                    </div>
                </div>

                <div class="ai-steps-mobile">
                    <span class="ai-step-m">
                        <b>{{ step }}</b>
                        {{ $t("ai_offer.step_" + step) }}
                    </span>
                    <span class="ai-dots">
                        <i v-for="s in steps" :key="s" :class="stepClass(s)"></i>
                    </span>
                </div>

                <div v-if="loading || result" class="ai-modal-body ai-gen" :class="{ done: !!result }">
                    <div class="ai-gen-side">
                        <div class="ai-gen-pct">
                            <span>{{ $t("ai_offer.gen_progress") }}</span>
                            <b>{{ genPct }} %</b>
                        </div>
                        <div v-for="g in genLines" :key="g.key" class="ai-gen-line" :class="g.cls">
                            <span class="mark">
                                <template v-if="g.cls === 'done'"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg></template>
                            </span>
                            <span class="txt">
                                <span class="ln">
                                    <span>{{ g.text }}</span>
                                    <small>{{ g.time }}</small>
                                </span>
                                <span v-if="g.cls === 'run'" class="sub"><i :style="{ width: g.p + '%' }"></i></span>
                            </span>
                        </div>
                        <div v-if="!result" class="ai-gen-tip">{{ $t("ai_offer.gen_tip") }}</div>
                        <div v-else-if="sheet.done" class="ai-gen-sum ai-reveal">
                            <b>{{ $t("ai_offer.gen_done_time", { s: result.secs }) }}</b>
                            <span>{{ $t("ai_offer.gen_done_items", resultCounts) }}</span>
                            <span v-if="resultCounts.e > 0">{{ $t("ai_offer.gen_done_est") }}</span>
                        </div>
                    </div>

                    <div class="ai-gen-stage">
                        <div class="ai-sheet">
                            <span v-if="sheet.done" class="stamp">{{ $t("ai_offer.gen_stamp") }}</span>
                            <div class="sheet-top">
                                <span class="logo">{{ $t("ai_offer.gen_logo") }}</span>
                                <span class="to">
                                    {{ $t("ai_offer.gen_offer_for") }}
                                    <b>{{ sheetKlient }}</b>
                                </span>
                            </div>
                            <div class="sheet-title">
                                <template v-if="sheet.title !== null">{{ sheet.title }}<span v-if="sheet.titleTyping" class="caret"></span></template>
                                <span v-else class="sk" style="width: 70%; height: 18px"></span>
                            </div>
                            <div class="sheet-intro">
                                <template v-if="sheet.intro !== null">{{ sheet.intro }}<span v-if="sheet.introTyping" class="caret"></span></template>
                                <template v-else>
                                    <span class="sk" style="width: 100%"></span>
                                    <span class="sk" style="width: 94%"></span>
                                    <span class="sk" style="width: 60%"></span>
                                </template>
                            </div>
                            <div class="sheet-head">
                                <span>{{ $t("ai_offer.gen_col_item") }}</span>
                                <span>{{ sheetDph ? $t("ai_offer.gen_col_price") : $t("ai_offer.gen_col_price_plain") }}</span>
                            </div>
                            <div class="sheet-rows">
                                <div v-for="r in sheet.rows" :key="r.key" class="sheet-row" :class="{ hot: r.hot }">
                                    <span class="n">
                                        <template v-if="r.name !== null">
                                            <span class="nm">{{ r.name }}</span>
                                            <small v-if="r.qty">{{ r.qty }}</small>
                                            <small v-if="r.odhad" class="est">{{ $t("ai_offer.gen_est") }}</small>
                                        </template>
                                        <span v-else class="sk" :style="{ width: r.w }"></span>
                                    </span>
                                    <span class="p">
                                        <template v-if="r.price !== null">{{ r.price }}</template>
                                        <span v-else class="sk" style="width: 64px"></span>
                                    </span>
                                </div>
                                <div v-if="sheet.more" class="sheet-more">{{ $t("ai_offer.gen_more", { n: sheet.more }) }}</div>
                            </div>
                            <div class="sheet-total" :class="{ flash: sheet.done }">
                                <span>{{ sheetDph ? $t("ai_offer.gen_total") : $t("ai_offer.gen_total_plain") }}</span>
                                <b v-if="sheet.total !== null">{{ sheet.total }}</b>
                                <span v-else class="sk" style="width: 90px; height: 18px"></span>
                            </div>
                            <div v-if="sheet.totalVat !== null" class="sheet-vat ai-reveal">
                                <span>{{ $t("ai_offer.gen_total_vat") }}</span>
                                <b>{{ sheet.totalVat }}</b>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else-if="booting" class="ai-modal-loading">
                    <div class="spinner"></div>
                </div>

                <div v-else-if="previewOpen && previewMode === 'inline'" class="ai-modal-body inline-preview">
                    <ai-offer-preview mode="inline" v-bind="previewProps" @close="previewOpen = false" />
                </div>

                <div v-else class="ai-modal-body" :class="'s' + step">
                <div v-if="step === 1" class="ai-cards">
                    <section class="ai-card-sec" :class="{ invalid: fieldErrors.odbory }">
                        <header class="ai-card-h">
                            <span class="num">1</span>
                            <span>
                                <b>{{ $t("ai_offer.card_areas_title") }}</b>
                                <small>{{ $t("ai_offer.card_areas_desc") }}</small>
                            </span>
                        </header>
                        <div class="ai-area-grid">
                            <button
                                v-for="g in areaGroups"
                                :key="g.id"
                                type="button"
                                class="ai-area-tile"
                                :class="{ open: openGroup === g.id, has: g.count > 0 }"
                                @click="pickGroup(g)"
                            >
                                <span class="ic">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="g.icon"></path></svg>
                                </span>
                                <span class="tx">
                                    <b>{{ g.label }}</b>
                                    <small v-if="g.count">{{ $t("ai_offer.area_group_count", { n: g.count }) }}</small>
                                </span>
                            </button>
                        </div>
                        <div v-if="openGroupAreas.length > 1" class="chips areas">
                            <span
                                v-for="slug in openGroupAreas"
                                :key="slug"
                                class="chip"
                                :class="{ on: hasArea(slug), off: !hasArea(slug) && areasFull }"
                                @click="toggleArea(slug)"
                                >{{ $t("ai_offer.area_" + slug) }}</span
                            >
                        </div>
                        <span v-if="fieldErrors.odbory" class="field-error">{{ fieldErrors.odbory }}</span>
                        <div v-if="hasArea('ine')" class="ai-field" :class="{ invalid: fieldErrors.odbor_iny }">
                            <input
                                ref="odborIny"
                                v-model="profile.odbor_iny"
                                type="text"
                                maxlength="120"
                                :placeholder="$t('ai_offer.field_other_area_ph')"
                                @input="clearError('odbor_iny')"
                            />
                            <label>{{ $t("ai_offer.field_other_area") }}</label>
                            <span v-if="fieldErrors.odbor_iny" class="field-error">{{ fieldErrors.odbor_iny }}</span>
                        </div>
                    </section>

                    <section class="ai-card-sec">
                        <header class="ai-card-h">
                            <span class="num">2</span>
                            <span>
                                <b>{{ $t("ai_offer.card_work_title") }}</b>
                                <small>{{ $t("ai_offer.card_work_desc") }}</small>
                            </span>
                        </header>
                        <div class="ai-grid-2">
                            <div class="ai-field" :class="{ invalid: fieldErrors.typicke_sluzby }">
                                <textarea
                                    v-model="profile.typicke_sluzby"
                                    maxlength="1000"
                                    :placeholder="ph('services')"
                                    @input="clearError('typicke_sluzby')"
                                ></textarea>
                                <label>{{ $t("ai_offer.field_services") }}</label>
                                <span v-if="fieldErrors.typicke_sluzby" class="field-error">
                                    {{ fieldErrors.typicke_sluzby }}
                                </span>
                            </div>
                            <div class="ai-field">
                                <textarea
                                    v-model="profile.co_nerobime"
                                    maxlength="1000"
                                    :placeholder="ph('excluded')"
                                ></textarea>
                                <label>{{ $t("ai_offer.field_excluded") }}</label>
                            </div>
                        </div>
                    </section>

                    <section class="ai-card-sec">
                        <button type="button" class="ai-card-h ai-card-toggle" :aria-expanded="String(optionalOpen)" @click="optionalOpen = !optionalOpen">
                            <span class="num">3</span>
                            <span>
                                <b>{{ $t("ai_offer.optional_title") }}</b>
                                <small v-if="!optionalOpen && optionalSummary" class="ai-card-sum">{{ optionalSummary }}</small>
                                <small v-else>{{ $t("ai_offer.optional_text") }}</small>
                            </span>
                            <span class="ai-card-more">
                                {{ optionalOpen ? $t("ai_offer.optional_hide") : $t("ai_offer.optional_show") }}
                                <svg :style="{ transform: optionalOpen ? 'rotate(180deg)' : 'none' }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>
                            </span>
                        </button>
                        <div v-if="optionalOpen" class="ai-grid-3 ai-reveal">
                            <div class="ai-field">
                                <input v-model="profile.hodinova_sadzba" type="number" step="0.01" min="0" placeholder="0" />
                                <label>{{ $t("ai_offer.field_rate", { currency: currency.symbol }) }}</label>
                            </div>
                            <div class="ai-field">
                                <input v-model="profile.marza" type="number" step="0.1" min="0" placeholder="0" />
                                <label>{{ $t("ai_offer.field_margin") }}</label>
                            </div>
                            <div class="ai-field">
                                <input v-model="profile.prevazujuca_jednotka" type="text" maxlength="50" :placeholder="ph('unit')" />
                                <label>{{ $t("ai_offer.field_unit") }}</label>
                            </div>
                            <div class="ai-field">
                                <input v-model="profile.region" type="text" maxlength="120" :placeholder="$t('ai_offer.field_region_ph')" />
                                <label>{{ $t("ai_offer.field_region") }}</label>
                            </div>
                            <div class="ai-field">
                                <ui-select v-model="profile.velkost_firmy" :options="velkostOptions" :placeholder="$t('ai_offer.size_none')" />
                                <label>{{ $t("ai_offer.field_size") }}</label>
                            </div>
                            <div class="ai-field">
                                <input v-model="profile.vlastne_pravidla" type="text" maxlength="1000" :placeholder="$t('ai_offer.field_rules_ph')" />
                                <label>{{ $t("ai_offer.field_rules") }}</label>
                            </div>
                        </div>
                    </section>
                </div>

                <div v-else-if="step === 2">
                    <div v-if="itemsEmpty && !picker" class="empty-state">
                        <div class="empty-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                                <line x1="7" y1="7" x2="7.01" y2="7"></line>
                            </svg>
                        </div>
                        <div>
                            <b>{{ $t("ai_offer.items_empty_title") }}</b>
                            <p>{{ $t("ai_offer.items_empty_text") }}</p>
                        </div>
                    </div>

                    <template v-else-if="!picker">
                        <div class="stat-row">
                            <div class="stat-box" :class="itemsStats.s_cenou > 0 ? 'ok' : 'warn'">
                                <div class="n">{{ itemsStats.s_cenou }}</div>
                                <div class="l">{{ $t("ai_offer.items_with_price") }}</div>
                            </div>
                            <div class="stat-box">
                                <div class="n">{{ itemsStats.bez_ceny }}</div>
                                <div class="l">{{ $t("ai_offer.items_without_price") }}</div>
                            </div>
                        </div>

                        <div v-if="itemsStats.ukazka.length" class="item-list">
                            <div class="head">
                                <span>{{ $t("ai_offer.items_table_name") }}</span>
                                <span>{{ $t("ai_offer.items_table_price") }}</span>
                            </div>
                            <div v-for="(it, i) in itemsStats.ukazka" :key="i">
                                <span>{{ it.nazov }}<template v-if="it.jednotka"> · {{ it.jednotka }}</template></span>
                                <span class="price">{{ formatCena(it.cena) }}</span>
                            </div>
                            <div v-if="itemsStats.s_cenou > itemsStats.ukazka.length" class="more">
                                {{
                                    $t("ai_offer.items_table_more", {
                                        count: itemsStats.s_cenou - itemsStats.ukazka.length,
                                    })
                                }}
                            </div>
                        </div>
                    </template>

                    <div v-if="picker" class="picker">
                        <div class="picker-head">
                            <b>{{ $t("ai_offer.items_pick_title") }}</b>
                            <span class="picker-count">
                                {{ $t("ai_offer.items_pick_selected", { count: pickedCount, total: picker.items.length }) }}
                            </span>
                        </div>

                        <div v-if="picker.loading" class="picker-loading">
                            <div class="spinner small"></div>
                            {{ $t("ai_offer.items_pick_loading") }}
                        </div>

                        <template v-else>
                            <div class="picker-tools">
                                <input
                                    v-model="picker.query"
                                    type="text"
                                    class="picker-search"
                                    :placeholder="$t('ai_offer.items_pick_search')"
                                />
                                <a @click="pickAll(true)">{{ $t("ai_offer.items_pick_all") }}</a>
                                <a @click="pickAll(false)">{{ $t("ai_offer.items_pick_none") }}</a>
                            </div>

                            <div class="picker-list">
                                <label v-for="it in pickerFiltered" :key="it.uuid" class="picker-row">
                                    <input v-model="picker.selected[it.uuid]" type="checkbox" class="native" />
                                    <span class="box" :class="{ on: picker.selected[it.uuid] }">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                    </span>
                                    <span class="name">
                                        {{ it.nazov }}<template v-if="it.jednotka"> · {{ it.jednotka }}</template>
                                    </span>
                                    <span class="price">{{ formatCena(it.cena) }}</span>
                                </label>
                                <div v-if="!pickerFiltered.length" class="picker-empty">
                                    {{ $t("ai_offer.items_pick_empty") }}
                                </div>
                            </div>

                            <div class="picker-foot">
                                <span class="btn-line" @click="closePicker">{{ $t("ai_offer.items_pick_cancel") }}</span>
                                <span
                                    class="btn-dark"
                                    :class="{ busy: importing, disabled: pickedCount === 0 }"
                                    @click="importPicked"
                                >
                                    {{ $t("ai_offer.items_pick_submit", { count: pickedCount }) }}
                                </span>
                            </div>
                        </template>
                    </div>

                    <div v-else-if="itemsStats.importable > 0" class="ai-card">
                        <p>
                            <b>{{ $t("ai_offer.items_import_title") }}</b><br />
                            <template v-if="itemsStats.s_cenou > 0">
                                {{ $t("ai_offer.items_import_more", { count: itemsStats.importable }) }}
                            </template>
                            <template v-else>
                                {{ $t("ai_offer.items_import_text", { count: itemsStats.importable }) }}
                            </template>
                        </p>
                        <span class="btn-dark" @click="openPicker">{{ $t("ai_offer.items_import_btn") }}</span>
                    </div>

                    <p v-if="importMessage" class="import-msg">✓ {{ importMessage }}</p>

                    <div v-if="!picker" class="ai-card dashed">
                        <p>
                            <b>{{ $t("ai_offer.items_manual_title") }}</b><br />
                            {{ $t("ai_offer.items_manual_text") }}
                        </p>
                        <a class="btn-line" href="/admin/items" target="_blank" rel="noopener">
                            {{ $t("ai_offer.items_manual_btn") }} ↗
                        </a>
                    </div>
                </div>
                <div v-else-if="step === 3" class="ai-cards">
                    <section class="ai-card-sec">
                        <header class="ai-card-h">
                            <span class="num">1</span>
                            <span>
                                <b>{{ $t("ai_offer.field_style") }}</b>
                                <small>{{ $t("ai_offer.modal_style_hint") }}</small>
                            </span>
                        </header>
                        <div class="chips">
                            <span
                                v-for="o in styly"
                                :key="o.value"
                                class="chip"
                                :class="{ on: profile.styl === o.value }"
                                @click="$set(profile, 'styl', o.value)"
                                >{{ o.label }}</span
                            >
                        </div>
                    </section>

                    <section class="ai-card-sec">
                        <header class="ai-card-h">
                            <span class="num">2</span>
                            <span>
                                <b>{{ $t("ai_offer.field_level") }}</b>
                                <small>{{ $t("ai_offer.card_level_desc") }}</small>
                            </span>
                        </header>
                        <div class="chips">
                            <span
                                v-for="o in hladiny"
                                :key="o.value"
                                class="chip"
                                :class="{ on: profile.hladina === o.value }"
                                @click="$set(profile, 'hladina', o.value)"
                                >{{ o.label }}</span
                            >
                        </div>
                    </section>

                    <section class="ai-card-sec">
                        <header class="ai-card-h">
                            <span class="num">3</span>
                            <span>
                                <b>{{ $t("ai_offer.card_texts_title") }}</b>
                                <small>{{ $t("ai_offer.card_texts_desc") }}</small>
                            </span>
                        </header>
                        <div class="ai-grid-3">
                            <div class="ai-field">
                                <ui-select v-model="profile.ton" :options="tony" />
                                <label>{{ $t("ai_offer.field_tone") }}</label>
                            </div>
                            <div class="ai-field">
                                <ui-select v-model="profile.oslovenie" :options="oslovenia" />
                                <label>{{ $t("ai_offer.field_address") }}</label>
                            </div>
                            <div class="ai-field">
                                <input v-model.number="profile.platnost_dni" type="number" min="1" max="365" placeholder="30" />
                                <label>{{ $t("ai_offer.field_validity") }}</label>
                            </div>
                        </div>
                        <div class="ai-grid-2">
                            <div class="ai-field">
                                <textarea v-model="profile.dodanie_text" maxlength="1000" :placeholder="$t('ai_offer.field_delivery_ph')"></textarea>
                                <label>{{ $t("ai_offer.field_delivery") }}</label>
                            </div>
                            <div class="ai-field">
                                <textarea v-model="profile.zaverecny_text" maxlength="2000" :placeholder="$t('ai_offer.field_closing_ph')"></textarea>
                                <label>{{ $t("ai_offer.field_closing") }}</label>
                                <span class="hint">{{ $t("ai_offer.field_closing_hint") }}</span>
                            </div>
                        </div>
                    </section>
                </div>
                <div v-else-if="step === 4" class="ai-step4">
                    <div v-if="newClient" class="new-client">
                        <div class="new-client-head">
                            <b>{{ $t("ai_offer.client_new_title") }}</b>
                            <span class="hint">{{ $t("ai_offer.client_ico_hint") }}</span>
                        </div>

                        <div class="ai-row">
                            <div class="ai-field ico-field">
                                <span class="ico-wrap">
                                    <input
                                        v-model="newClient.ico"
                                        type="text"
                                        inputmode="numeric"
                                        maxlength="12"
                                        placeholder="12345678"
                                        @input="onIcoInput"
                                    />
                                    <span class="lookup" :class="{ busy: lookup.loading }" @click="lookupIco">
                                        {{ lookup.loading ? $t("ai_offer.client_lookup_loading") : $t("ai_offer.client_lookup") }}
                                    </span>
                                </span>
                                <label>{{ $t("ai_offer.client_ico") }}</label>
                                <span v-if="lookup.message" class="hint" :class="{ ok: lookup.ok, empty: !lookup.ok }">
                                    {{ lookup.message }}
                                </span>
                            </div>
                            <div class="ai-field" :class="{ invalid: newClient.error }">
                                <input
                                    ref="newClientName"
                                    v-model="newClient.nazov"
                                    type="text"
                                    maxlength="255"
                                    :placeholder="$t('ai_offer.client_name_ph')"
                                    @input="newClient.error = ''"
                                />
                                <label>{{ $t("ai_offer.client_name") }}</label>
                                <span v-if="newClient.error" class="field-error">{{ newClient.error }}</span>
                            </div>
                        </div>

                        <div class="ai-row">
                            <div class="ai-field">
                                <input v-model="newClient.ulica" type="text" maxlength="255" />
                                <label>{{ $t("ai_offer.client_street") }}</label>
                            </div>
                            <div class="ai-field zip">
                                <input v-model="newClient.psc" type="text" maxlength="10" />
                                <label>{{ $t("ai_offer.client_zip") }}</label>
                            </div>
                            <div class="ai-field">
                                <input v-model="newClient.mesto" type="text" maxlength="255" />
                                <label>{{ $t("ai_offer.client_city") }}</label>
                            </div>
                        </div>

                        <div class="ai-row">
                            <div class="ai-field">
                                <input v-model="newClient.dic" type="text" maxlength="20" />
                                <label>{{ $t("ai_offer.client_dic") }}</label>
                            </div>
                            <div class="ai-field">
                                <input v-model="newClient.icdph" type="text" maxlength="20" />
                                <label>{{ $t("ai_offer.client_icdph") }}</label>
                            </div>
                        </div>

                        <div class="ai-row">
                            <div class="ai-field">
                                <input v-model="newClient.osoba" type="text" maxlength="255" />
                                <label>{{ $t("ai_offer.client_person") }}</label>
                            </div>
                            <div class="ai-field">
                                <input v-model="newClient.email" type="email" maxlength="255" />
                                <label>{{ $t("ai_offer.client_email") }}</label>
                            </div>
                        </div>

                        <div class="new-client-foot">
                            <span v-if="fieldErrors.klient_id" class="field-error">{{ fieldErrors.klient_id }}</span>
                            <span class="btn-line" @click="closeNewClient">{{ $t("ai_offer.client_cancel") }}</span>
                            <span class="btn-dark" :class="{ busy: newClient.saving }" @click="saveNewClient">
                                {{ $t("ai_offer.client_save") }}
                            </span>
                        </div>
                    </div>


                    <div class="ai-grid-2">
                        <div v-if="!newClient" class="ai-field" :class="{ invalid: fieldErrors.klient_id }">
                            <ui-select
                                v-model="form.klient_id"
                                :options="klientOptions"
                                icon="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"
                                :placeholder="$t('ai_offer.modal_client_ph')"
                                :search-placeholder="$t('ai_offer.modal_client_search')"
                                :empty-text="klientiLoaded ? $t('ai_offer.modal_client_none_found') : '…'"
                                :invalid="!!fieldErrors.klient_id"
                                searchable
                                @change="onKlientPicked"
                            >
                                <template #footer>
                                    <span class="select-action" @click="openNewClient">
                                        + {{ $t("ai_offer.modal_client_new") }}
                                    </span>
                                </template>
                            </ui-select>
                            <label>{{ $t("ai_offer.modal_client") }}</label>
                            <span v-if="fieldErrors.klient_id" class="field-error">{{ fieldErrors.klient_id }}</span>
                            <span v-else class="field-foot">
                                <span v-if="klientSuggested" class="ai-suggest"><svg class="spark" width="13" height="13" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true"><path d="M9 1.5L10.6 6.4L15.5 8L10.6 9.6L9 14.5L7.4 9.6L2.5 8L7.4 6.4L9 1.5Z"></path></svg>{{ $t("ai_offer.judge_client") }}</span>
                                <span class="new-client-link" @click="openNewClient">
                                    + {{ $t("ai_offer.modal_client_new") }}
                                </span>
                            </span>
                        </div>

                        <div class="ai-field">
                            <ui-select
                                v-model="form.vzor_uuid"
                                :options="vzorOptions"
                                icon="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM9 8h6M9 12h6M9 16h3"
                                :placeholder="$t('ai_offer.template_none')"
                                :search-placeholder="$t('ai_offer.template_search')"
                                :empty-text="templatesLoaded ? $t('ai_offer.template_empty') : '…'"
                                searchable
                                @change="vzorTouched = true"
                            />
                            <label>{{ $t("ai_offer.template_label") }}</label>
                            <span v-if="vzorSuggestion" class="ai-suggest action" @click="useSuggestedVzor">
                                <svg class="spark" width="13" height="13" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true"><path d="M9 1.5L10.6 6.4L15.5 8L10.6 9.6L9 14.5L7.4 9.6L2.5 8L7.4 6.4L9 1.5Z"></path></svg>{{ $t("ai_offer.template_suggest", { nazov: vzorSuggestion }) }}
                                <b>{{ $t("ai_offer.template_use") }}</b>
                            </span>
                            <span v-else class="hint">{{ $t("ai_offer.template_hint") }}</span>
                        </div>
                    </div>

                    <div class="ai-field task" :class="{ invalid: fieldErrors.zadanie }">
                        <span class="with-icon area">
                            <svg class="fi" width="16" height="16" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true"><path d="M9 1.5L10.6 6.4L15.5 8L10.6 9.6L9 14.5L7.4 9.6L2.5 8L7.4 6.4L9 1.5Z"></path></svg>
                            <textarea
                                ref="task"
                                v-model="form.zadanie"
                                maxlength="4000"
                                :placeholder="ph('task')"
                                @input="onTaskInput"
                            ></textarea>
                        </span>
                        <label>{{ $t("ai_offer.modal_task") }}</label>
                        <span v-if="fieldErrors.zadanie" class="field-error">{{ fieldErrors.zadanie }}</span>
                        <div v-else-if="judge.reading || judge.available" class="judge-strip">
                            <span v-if="judge.reading" class="judge-reading">
                                <span class="judge-dots"><i></i><i></i><i></i></span>
                                {{ $t("ai_offer.judge_reading") }}
                            </span>
                            <template v-else-if="judge.chyba.length">
                                <span
                                    v-for="c in judge.chyba"
                                    :key="c"
                                    class="judge-chip"
                                    @click="appendMissing(c)"
                                >
                                    {{ $t("ai_offer.judge_missing") }} {{ $t("ai_offer.judge_missing_" + c) }}
                                    <b>· {{ $t("ai_offer.judge_add") }}</b>
                                </span>
                            </template>
                            <span v-else class="judge-ok">✓ {{ $t("ai_offer.judge_ok") }}</span>
                        </div>
                    </div>

                    <div v-if="judgeItems.length" class="judge-items">
                        <span class="judge-label"><svg class="spark" width="13" height="13" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true"><path d="M9 1.5L10.6 6.4L15.5 8L10.6 9.6L9 14.5L7.4 9.6L2.5 8L7.4 6.4L9 1.5Z"></path></svg>{{ $t("ai_offer.judge_items") }}</span>
                        <span v-for="it in judgeItemsShown" :key="it.id" class="judge-item">
                            {{ it.nazov }}
                            <button type="button" :aria-label="$t('ai_offer.judge_item_remove')" @click="excludeItem(it.id)">×</button>
                        </span>
                        <span v-if="judgeItems.length > judgeItemsShown.length" class="judge-more">
                            +{{ judgeItems.length - judgeItemsShown.length }}
                        </span>
                    </div>

                    <div class="ai-grid-2">
                        <div class="ai-field budget" :class="{ invalid: fieldErrors.cielova_cena }">
                            <span class="with-icon">
                                <span class="fi cur">{{ currency.symbol }}</span>
                                <input
                                    v-model="form.cielova_cena"
                                    type="number"
                                    min="1"
                                    step="1"
                                    :placeholder="$t('ai_offer.field_budget_ph')"
                                    @input="clearError('cielova_cena')"
                                />
                            </span>
                            <label>{{ $t("ai_offer.field_budget", { currency: currency.symbol }) }}</label>
                            <span v-if="fieldErrors.cielova_cena" class="field-error">{{ fieldErrors.cielova_cena }}</span>
                            <span v-else class="hint">{{ $t("ai_offer.field_budget_hint") }}</span>
                        </div>

                        <div class="ai-field">
                            <div class="ai-seg" role="group" :aria-label="$t('ai_offer.modal_count')">
                                <button
                                    v-for="o in pocty"
                                    :key="o.value"
                                    type="button"
                                    class="chip"
                                    :class="{ on: form.pocet_poloziek === o.value }"
                                    @click="pickPocet(o.value)"
                                >
                                    {{ o.label }}
                                </button>
                            </div>
                            <label>{{ $t("ai_offer.modal_count") }}</label>
                            <span v-if="pocetSuggested" class="ai-suggest"><svg class="spark" width="13" height="13" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true"><path d="M9 1.5L10.6 6.4L15.5 8L10.6 9.6L9 14.5L7.4 9.6L2.5 8L7.4 6.4L9 1.5Z"></path></svg>{{ $t("ai_offer.judge_count") }}</span>
                        </div>
                    </div>

                    <div class="preset-box">
                        <div v-if="styleSuggestion" class="ai-suggest action style" @click="useSuggestedStyle">
                            <svg class="spark" width="13" height="13" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true"><path d="M9 1.5L10.6 6.4L15.5 8L10.6 9.6L9 14.5L7.4 9.6L2.5 8L7.4 6.4L9 1.5Z"></path></svg>{{ $t("ai_offer.judge_style", { styl: styleSuggestion.label }) }}
                            <b>{{ $t("ai_offer.judge_style_use") }}</b>
                        </div>
                        <div class="t">
                            <span>{{ presetSummary }}</span>
                            <a @click="toggleOverride">
                                {{ override ? $t("ai_offer.preset_hide") : $t("ai_offer.preset_edit") }}
                            </a>
                        </div>

                        <div v-if="override" class="preset-edit">
                            <div class="chips">
                                <span
                                    v-for="o in styly"
                                    :key="o.value"
                                    class="chip"
                                    :class="{ on: override.styl === o.value }"
                                    @click="$set(override, 'styl', o.value)"
                                    >{{ o.label }}</span
                                >
                            </div>
                            <div class="chips">
                                <span
                                    v-for="o in hladiny"
                                    :key="o.value"
                                    class="chip"
                                    :class="{ on: override.hladina === o.value }"
                                    @click="$set(override, 'hladina', o.value)"
                                    >{{ o.label }}</span
                                >
                            </div>
                            <div class="chips">
                                <span
                                    v-for="o in tony"
                                    :key="o.value"
                                    class="chip"
                                    :class="{ on: override.ton === o.value }"
                                    @click="$set(override, 'ton', o.value)"
                                    >{{ o.label }}</span
                                >
                            </div>
                            <span class="hint">{{ $t("ai_offer.preset_note") }}</span>
                        </div>
                    </div>
                </div>

                    <p v-if="error" class="error">{{ error }}</p>
                </div>

                <div v-if="loading || result" class="ai-modal-foot">
                    <template v-if="!result">
                        <span class="hint">{{ $t("ai_offer.gen_rows_hint") }}</span>
                        <button type="button" class="ai-btn2" @click="close">{{ $t("ai_offer.gen_minimize") }}</button>
                    </template>
                    <template v-else>
                        <span class="back" @click="dismissResult">{{ $t("ai_offer.gen_close") }}</span>
                        <!-- tlacidla su v DOM od zaciatku, aby patka pri dokonceni neposkocila -->
                        <span class="foot-right" :class="sheet.done ? 'ai-reveal' : 'ai-foot-wait'">
                            <a class="ai-btn2" :href="result.viewUrl" target="_blank" rel="noopener"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>{{ $t("ai_offer.gen_view") }}</a>
                            <a class="ai-btn1" :href="result.editUrl"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"></path><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"></path></svg>{{ $t("ai_offer.gen_edit") }}</a>
                        </span>
                    </template>
                </div>
                <div v-else-if="!booting" class="ai-modal-foot">
                    <span v-if="step > 1" class="back" @click="goTo(step - 1)">
                        ← {{ $t("ai_offer.step_back") }}
                    </span>
                    <span v-else class="back" @click="close">{{ $t("ai_offer.modal_cancel") }}</span>

                    <span class="foot-right">
                        <button v-if="summaryChip" type="button" class="ai-summary" @click="togglePreview">
                            {{ summaryChip }}
                        </button>
                        <span v-else-if="footHint" class="hint">{{ footHint }}</span>
                        <span class="submit" :class="{ busy: saving, ai: step === 4 }" @click="onPrimary">
                            <template v-if="step === 4"><svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true"><path d="M9 1.5L10.6 6.4L15.5 8L10.6 9.6L9 14.5L7.4 9.6L2.5 8L7.4 6.4L9 1.5Z"></path><path d="M14.75 12.25L15.4 14.1L17.25 14.75L15.4 15.4L14.75 17.25L14.1 15.4L12.25 14.75L14.1 14.1L14.75 12.25Z"></path></svg></template>
                            {{ step === 4 ? $t("ai_offer.modal_submit") : $t("ai_offer.step_next") }}
                        </span>
                    </span>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import { EventBus } from "./EventBus.js";
import UiSelect from "./UiSelect.vue";
import AiOfferPreview from "./AiOfferPreview.vue";

const STEPS = [1, 2, 3, 4];

// Placeholdre v krokoch 1 a 4 podla prveho zvoleneho odboru — IT firme nema
// napovedat rekonstrukciu kupelne. Kluce su ai_offer.ph.<skupina>.<pole>.
const PH_SKUPINY = {
    stavebnictvo: "stavba",
    rekonstrukcie: "stavba",
    obklady_dlazby: "stavba",
    maliarske_prace: "stavba",
    strechy: "stavba",
    stolarstvo: "stavba",
    podlahy: "stavba",
    okna_dvere: "stavba",
    zateplovanie: "stavba",
    elektroinstalacie: "technika",
    voda_kurenie_plyn: "technika",
    fotovoltika: "technika",
    klimatizacie: "technika",
    zahrada: "domov",
    upratovanie: "domov",
    stahovanie: "domov",
    autoservis: "auto",
    it_sluzby: "it",
    web_vyvoj: "it",
    marketing: "kreativa",
    grafika: "kreativa",
    foto_video: "kreativa",
    uctovnictvo: "kancelaria",
    pravne_sluzby: "kancelaria",
    poradenstvo: "kancelaria",
    skolenia: "kancelaria",
    gastro: "gastro",
};

// Od tejto sirky okna sa nahlad vysunie vedla sprievodcu, pod nou nahradi obsah kroku.
const PREVIEW_SIDE_MIN = 1530;
// Generovanie je jedna poziadavka — kroky v UI posuvame casom, posledny caka na odpoved.
const GEN_STEP_MS = 3500;
// Zaverecna obrazovka: kolko poloziek sa vypise na papier ponuky, zvysok je "+ dalsie"
const SHEET_ROWS = 6;
const SHEET_SK = ["62%", "78%", "50%", "70%", "84%"];
const NUM_LOCALE = { sk: "sk-SK", cz: "cs-CZ", en: "en-GB", de: "de-DE" };

// Kolko ms po poslednom znaku sa zadanie posle na posudok.
const JUDGE_DEBOUNCE = 700;
const JUDGE_MIN_LENGTH = 15;
const ROZSAH_NA_POCET = { maly: "3-5", stredny: "6-10", velky: "11-20" };
const STYLE_SUGGEST_MIN = 0.6;

// Dlazdice v kroku 1: skupiny odborov s ikonou. Klik na skupinu s viacerymi odbormi
// rozbali ich ako cipy, skupina s jednym odborom ho rovno prepne.
const AREA_GROUPS = [
    { id: "stavba", icon: "M3 21h18M5 21V10l7-5 7 5v11M9 21v-6h6v6" },
    { id: "technika", icon: "M13 2L4 14h7l-1 8 9-12h-7l1-8z" },
    { id: "domov", icon: "M12 22V12M12 12c0-4 3-7 7-7 0 4-3 7-7 7zM12 14c0-3-2-5-5-5 0 3 2 5 5 5z" },
    { id: "auto", icon: "M5 17h14M6.5 17l1.5-5h8l1.5 5M7 17v2M17 17v2M8.5 12l1-3h5l1 3" },
    { id: "it", icon: "M3 5h18v12H3zM8 21h8M12 17v4" },
    { id: "kreativa", icon: "M12 3a9 9 0 1 0 0 18c1 0 2-1 2-2 0-1.5-1-2-1-3s1-2 2-2h2a4 4 0 0 0 4-4c0-4-4-7-9-7z" },
    { id: "kancelaria", icon: "M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM9 8h6M9 12h6M9 16h3" },
    { id: "gastro", icon: "M4 3v8a3 3 0 0 0 6 0V3M7 3v18M17 3c-2 2-3 5-3 8h3v10" },
    { id: "ine", icon: "M12 5v14M5 12h14" },
];

function prazdnyPosudok() {
    return { available: false, reading: false, chyba: [], polozky: [], klient: null, vzor: null, rozsah: null, styl: null };
}

function prazdnyKlient() {
    return {
        nazov: "",
        ico: "",
        dic: "",
        icdph: "",
        ulica: "",
        psc: "",
        mesto: "",
        osoba: "",
        email: "",
        spolocnost: "",
        error: "",
        saving: false,
    };
}

export default {
    name: "AiOfferModal",
    components: { UiSelect, AiOfferPreview },
    data() {
        return {
            open: false,
            booting: true,
            loading: false,
            saving: false,
            error: "",
            step: 1,
            steps: STEPS,
            isNarrow: false,
            completedSteps: [],
            businessAreas: [],
            currency: { code: "EUR", symbol: "€" },
            itemsStats: { s_cenou: 0, bez_ceny: 0, importable: 0, ukazka: [] },
            profile: {},
            fieldErrors: {},
            override: null,
            importing: false,
            importMessage: "",
            // vyber poloziek na doplnenie v kroku 2: { loading, items, selected, query }
            picker: null,
            // formular noveho klienta v kroku 4
            newClient: null,
            lookup: { loading: false, message: "", ok: false, ico: "" },
            velkosti: ["sam", "maly_tim", "stredna", "velka"],
            klienti: [],
            klientiLoaded: false,
            form: {
                klient_id: null,
                zadanie: "",
                pocet_poloziek: "6-10",
                cielova_cena: "",
                vzor_uuid: null,
            },
            templates: [],
            templatesLoaded: false,
            previewOpen: false,
            openGroup: null,
            optionalOpen: false,
            viewportW: window.innerWidth,
            genStage: 0,
            // casy generovania pre priebeh vlavo a postupne vypisanie hotovej ponuky
            genStart: 0,
            genNow: 0,
            genStageAt: [],
            genTick: null,
            revealAt: 0,
            genTimer: null,
            result: null,
            firma: (document.querySelector('meta[name="firma"]') || {}).content || "",
            // posudky nad zadanim (Jev) — len napovedy, formular bez nich funguje
            judge: prazdnyPosudok(),
            judgeSeq: 0,
            judgeTimer: null,
            klientTouched: false,
            klientSuggested: false,
            pocetTouched: false,
            pocetSuggested: false,
            vzorTouched: false,
            excludedItems: [],
        };
    },
    watch: {
        // Pozadie stranky sa pod otvorenym oknom nescrolluje
        open(val) {
            document.documentElement.classList.toggle("ai-scroll-lock", val);
        },
    },
    computed: {
        headTitle() {
            if (this.result) {
                const k = this.result.summary.klient;
                return k ? this.$t("ai_offer.gen_done_title", { klient: k }) : this.$t("ai_offer.gen_done_title_plain");
            }
            if (this.loading) return this.$t("ai_offer.gen_title");
            return this.$t("ai_offer.step_" + this.step + "_title");
        },
        headKicker() {
            if (this.result) return this.$t("ai_offer.gen_done_kicker");
            if (this.loading) return this.$t("ai_offer.gen_kicker");
            return this.$t("ai_offer.step_kicker", { n: this.step });
        },
        headLead() {
            if (this.result) return this.$t("ai_offer.gen_done_lead");
            if (this.loading) return this.$t("ai_offer.gen_lead");
            return this.stepIntro;
        },
        previewMode() {
            return this.viewportW >= PREVIEW_SIDE_MIN ? "side" : "inline";
        },
        klientNazov() {
            const k = this.klienti.filter((x) => x.id === this.form.klient_id)[0];
            return k ? k.nazov : "";
        },
        vzorNazov() {
            const t = this.templates.filter((x) => x.uuid === this.form.vzor_uuid)[0];
            return t ? t.nazov : "";
        },
        previewProps() {
            return {
                firma: this.firma,
                klient: this.klientNazov,
                zadanie: this.form.zadanie,
                rozpocet: this.form.cielova_cena,
                mena: this.currency.symbol,
                polozky: this.judgeItems,
                pocet: this.form.pocet_poloziek,
                vzor: this.vzorNazov,
            };
        },
        // Kratky suhrn v paticke kroku 4 — klik otvori nahlad.
        summaryChip() {
            if (this.step !== 4) return "";
            const casti = [];
            const suma = Number(String(this.form.cielova_cena || "").replace(/\s/g, ""));
            if (suma > 0) casti.push("cca " + suma.toLocaleString("sk-SK") + " " + this.currency.symbol);
            if (this.klientNazov) casti.push(this.klientNazov);
            const pocet = this.pocty.filter((o) => o.value === this.form.pocet_poloziek)[0];
            if (pocet && this.form.pocet_poloziek !== "neriesit") {
                casti.push(this.$t("ai_offer.summary_items", { pocet: pocet.label }));
            }
            return casti.join(" · ");
        },
        genLines() {
            const at = this.genStageAt;
            const end = this.result ? this.result.at : 0;
            return [1, 2, 3, 4].map((n, i) => {
                const done = !!this.result || this.genStage > i;
                const run = !this.result && this.genStage === i;
                const od = at[i];
                const po = at[i + 1] || end;
                let p = 0;
                if (run && od) {
                    // posledny krok trva az do odpovede servera, preto sa k 100 % len blizi
                    const t = this.genNow - od;
                    p = i < 3 ? Math.min(100, (t / GEN_STEP_MS) * 100) : 100 * (1 - Math.exp(-t / 9000));
                }
                return {
                    key: n,
                    text: this.$t("ai_offer.gen_" + n),
                    cls: done ? "done" : run ? "run" : "wait",
                    time: done && od && po ? Math.max(1, Math.round((po - od) / 1000)) + " s" : "",
                    p: Math.round(p),
                };
            });
        },
        genPct() {
            if (this.result) return 100;
            if (!this.genStart) return 0;
            const t = (this.genNow - this.genStart) / 1000;
            return Math.round(95 * (1 - Math.exp(-t / 15)));
        },
        // Popisky sum ako na verejnej stranke: "bez DPH" len ked ponuka DPH ma.
        // Pocas generovania to este nevieme, preto neutralne "Cena" / "Spolu".
        sheetDph() {
            return !!(this.result && this.result.summary.s_dph);
        },
        sheetKlient() {
            return this.result ? this.result.summary.klient : this.klientNazov;
        },
        resultCounts() {
            const polozky = this.result ? this.result.summary.polozky : [];
            const e = polozky.filter((p) => p.odhad).length;
            return { n: polozky.length, c: polozky.length - e, e: e };
        },
        // Papier ponuky: pocas generovania sive ciary, po odpovedi sa hotova ponuka
        // postupne vypise (nazov, polozky s cenami, uvod, suma).
        sheet() {
            const r = this.result;
            if (!r) {
                return {
                    title: null, intro: null, total: null, totalVat: null, done: false, more: 0, titleTyping: false, introTyping: false,
                    rows: SHEET_SK.map((w, i) => ({ key: i, w: w, name: null, price: null, qty: "", odhad: false, hot: false })),
                };
            }
            const t = this.genNow - this.revealAt;
            const clamp = (x) => Math.max(0, Math.min(1, x));
            const ease = (x) => 1 - Math.pow(1 - x, 3);
            const pis = (txt, od, dlzka) => (t < od ? null : txt.slice(0, Math.round(txt.length * clamp((t - od) / dlzka))));

            const polozky = r.summary.polozky.slice(0, SHEET_ROWS);
            const rows = polozky.map((p, i) => {
                const nOd = 450 + i * 230;
                const cOd = nOd + 220;
                const cena = clamp((t - cOd) / 420);
                return {
                    key: i,
                    w: SHEET_SK[i % SHEET_SK.length],
                    name: pis(p.nazov, nOd, 280),
                    qty: t >= nOd + 280 && Number(p.pocet) !== 1 ? this.fmtNum(p.pocet) + " " + (p.jednotka || "") : "",
                    odhad: p.odhad && cena >= 1,
                    price: t < cOd ? null : this.fmtMoney(cena < 1 ? Math.round(p.cena * ease(cena)) : p.cena),
                    hot: t >= nOd && cena < 1,
                };
            });
            const koniecRiadkov = 450 + polozky.length * 230 + 640;
            const uvod = r.summary.uvod || "";
            const uvodDlzka = Math.min(1400, 300 + uvod.length * 6);
            const sumaOd = koniecRiadkov;
            const suma = clamp((t - sumaOd) / 600);
            const hotovo = t >= Math.max(koniecRiadkov + uvodDlzka, sumaOd + 600) + 120;
            return {
                title: pis(r.summary.nazov || "", 0, 520),
                titleTyping: t < 620,
                intro: pis(uvod, koniecRiadkov - 200, uvodDlzka),
                introTyping: t >= koniecRiadkov - 200 && t < koniecRiadkov - 100 + uvodDlzka,
                rows: rows,
                more: Math.max(0, r.summary.polozky.length - SHEET_ROWS),
                total: t < sumaOd ? null : this.fmtMoney(suma < 1 ? Math.round(r.summary.spolu * ease(suma)) : r.summary.spolu),
                totalVat: r.summary.s_dph && suma >= 1 ? this.fmtMoney(r.summary.spolu_s_dph) : null,
                done: hotovo,
            };
        },
        stepIntro() {
            const key = "ai_offer.step_" + this.step + "_intro";
            const text = this.$t(key);
            return text === key ? "" : text;
        },
        footHint() {
            if (this.step === 2 && this.itemsStats.s_cenou === 0) {
                return this.$t("ai_offer.items_empty_hint");
            }
            return "";
        },
        styly() {
            return [
                { value: "elegantna", label: this.$t("ai_offer.modal_style_eleg") },
                { value: "minimalisticka", label: this.$t("ai_offer.modal_style_min") },
                { value: "detailna", label: this.$t("ai_offer.modal_style_det") },
                { value: "technicka", label: this.$t("ai_offer.modal_style_tech") },
            ];
        },
        hladiny() {
            return [
                { value: "ekonomicka", label: this.$t("ai_offer.modal_level_eco") },
                { value: "standardna", label: this.$t("ai_offer.modal_level_std") },
                { value: "premiova", label: this.$t("ai_offer.modal_level_prem") },
            ];
        },
        tony() {
            return [
                { value: "predajny", label: this.$t("ai_offer.tone_predajny") },
                { value: "vecny", label: this.$t("ai_offer.tone_vecny") },
                { value: "priatelsky", label: this.$t("ai_offer.tone_priatelsky") },
            ];
        },
        oslovenia() {
            return [
                { value: "vykanie", label: this.$t("ai_offer.address_vykanie") },
                { value: "tykanie", label: this.$t("ai_offer.address_tykanie") },
            ];
        },
        pocty() {
            return [
                { value: "3-5", label: this.$t("ai_offer.modal_count_35") },
                { value: "6-10", label: this.$t("ai_offer.modal_count_610") },
                { value: "11-20", label: this.$t("ai_offer.modal_count_1120") },
                { value: "neriesit", label: this.$t("ai_offer.modal_count_any") },
            ];
        },
        presetSummary() {
            const zdroj = this.override || this.profile;
            const najdi = (zoznam, hodnota) => {
                const found = zoznam.filter((o) => o.value === hodnota)[0];
                return found ? found.label : "—";
            };

            return this.$t("ai_offer.preset_summary", {
                styl: najdi(this.styly, zdroj.styl),
                hladina: najdi(this.hladiny, zdroj.hladina),
                ton: najdi(this.tony, zdroj.ton),
            });
        },
        areasFull() {
            return (this.profile.odbory || []).length >= 3;
        },
        // Suhrn vyplnenych nepovinnych udajov pre zbalenu kartu 3
        optionalSummary() {
            const p = this.profile;
            const ma = (v) => v !== null && v !== undefined && String(v).trim() !== "";
            const cislo = (v) => (this.$i18n.locale === "en" ? String(v) : String(v).replace(".", ","));
            const casti = [];
            if (ma(p.hodinova_sadzba)) casti.push(cislo(p.hodinova_sadzba) + " " + this.currency.symbol + "/h");
            if (ma(p.marza)) casti.push(cislo(p.marza) + " %");
            if (ma(p.prevazujuca_jednotka)) casti.push(p.prevazujuca_jednotka);
            if (ma(p.region)) casti.push(p.region);
            if (ma(p.velkost_firmy)) casti.push(this.$t("ai_offer.size_" + p.velkost_firmy));
            if (ma(p.vlastne_pravidla)) casti.push(this.$t("ai_offer.field_rules"));
            return casti.join(" · ");
        },
        velkostOptions() {
            return [{ value: null, label: this.$t("ai_offer.size_none") }].concat(
                this.velkosti.map((v) => ({ value: v, label: this.$t("ai_offer.size_" + v) }))
            );
        },
        klientOptions() {
            return this.klienti.map((k) => ({ value: k.id, label: k.nazov, hint: k.mesto || "" }));
        },
        // Ziadne polozky v cenniku a nic na doplnenie z ponuk — typicky novy ucet.
        itemsEmpty() {
            const st = this.itemsStats;
            return st.s_cenou === 0 && st.bez_ceny === 0 && st.importable === 0;
        },
        phGroup() {
            const prvy = (this.profile.odbory || [])[0];
            return PH_SKUPINY[prvy] || "vseobecne";
        },
        pickerFiltered() {
            if (!this.picker) return [];
            const q = String(this.picker.query || "").toLowerCase().trim();
            if (!q) return this.picker.items;
            return this.picker.items.filter((it) => String(it.nazov).toLowerCase().indexOf(q) !== -1);
        },
        vzorOptions() {
            return [{ value: null, label: this.$t("ai_offer.template_none") }].concat(
                this.templates.map((t) => ({ value: t.uuid, label: t.nazov, hint: t.klient || "" }))
            );
        },
        vzorSuggestion() {
            const v = this.judge.vzor;
            if (!v || !v.uuid || this.vzorTouched || this.form.vzor_uuid === v.uuid) return "";
            return v.nazov || "";
        },
        judgeItems() {
            return this.judge.polozky.filter((p) => this.excludedItems.indexOf(p.id) === -1);
        },
        judgeItemsShown() {
            return this.judgeItems.slice(0, 6);
        },
        styleSuggestion() {
            const st = this.judge.styl;
            if (!st || !st.hodnota || st.istota < STYLE_SUGGEST_MIN) return null;
            const current = (this.override || this.profile).styl;
            if (st.hodnota === current) return null;
            const found = this.styly.filter((o) => o.value === st.hodnota)[0];
            return found ? { value: found.value, label: found.label } : null;
        },
        areaGroups() {
            return AREA_GROUPS.map((g) => {
                const slugs = this.areasOfGroup(g.id);
                const count = slugs.filter((x) => this.hasArea(x)).length;
                return { id: g.id, icon: g.icon, slugs: slugs, count: count, label: this.$t("ai_offer.area_group_" + g.id) };
            }).filter((g) => g.slugs.length);
        },
        openGroupAreas() {
            return this.openGroup ? this.areasOfGroup(this.openGroup) : [];
        },
        pickedCount() {
            if (!this.picker) return 0;
            return Object.keys(this.picker.selected).filter((k) => this.picker.selected[k]).length;
        },
    },
    mounted() {
        EventBus.$on("open-ai-offer-modal", this.show);
        window.addEventListener("resize", this.onResize);
        this.onResize();
    },
    beforeDestroy() {
        clearTimeout(this.judgeTimer);
        clearInterval(this.genTimer);
        EventBus.$off("open-ai-offer-modal", this.show);
        clearInterval(this.genTick);
        window.removeEventListener("resize", this.onResize);
        document.documentElement.classList.remove("ai-scroll-lock");
    },
    methods: {
        onResize() {
            this.isNarrow = window.innerWidth < 768;
            this.viewportW = window.innerWidth;
        },
        togglePreview() {
            this.previewOpen = !this.previewOpen;
        },
        // Stav pre asistenta v rohu: ci je okno otvorene, ci sa generuje a co je rozpracovane.
        emitStatus() {
            let phase = "idle";
            if (this.loading) phase = "generating";
            else if (this.result) phase = "done";
            else if (String(this.form.zadanie || "").trim() || this.form.klient_id) phase = "draft";

            const zadanie = String(this.form.zadanie || "").trim();
            const title = this.klientNazov || (zadanie.length > 60 ? zadanie.slice(0, 60) + "…" : zadanie);

            EventBus.$emit("ai-offer-status", {
                open: this.open,
                phase: phase,
                title: title,
                editUrl: this.result ? this.result.editUrl : "",
            });
        },
        // Z tlacidla prichadza bez parametra, z nastaveni uctu s cislom kroku.
        show(step) {
            const forced = typeof step === "number";

            // Generovanie bezi na pozadi alebo uz skoncilo — len vratime okno, nic neresetujeme.
            if (this.loading || this.result) {
                this.open = true;
                this.emitStatus();
                return;
            }

            this.result = null;
            this.open = true;
            this.booting = true;
            this.error = "";
            this.step = forced ? step : 1;
            this.picker = null;
            this.newClient = null;
            this.importMessage = "";
            this.fieldErrors = {};
            this.resetJudge();
            this.loadProfile(forced);
            this.loadTemplates();
            // Rozpisane zadanie z minula — napovedy ukazeme hned, nie az po dalsom pisani.
            if (String(this.form.zadanie || "").trim().length >= JUDGE_MIN_LENGTH) {
                this.judgeTimer = setTimeout(this.runJudge, JUDGE_DEBOUNCE);
            }
            this.loadKlienti();
            this.emitStatus();
        },
        // Hodiny pre priebeh a vypisanie ponuky; po dovypisani sa zastavia.
        startTick() {
            clearInterval(this.genTick);
            this.genTick = setInterval(() => {
                this.genNow = Date.now();
                if (this.result && this.sheet.done) clearInterval(this.genTick);
            }, 50);
        },
        // Zavriet hotovu ponuku: zadanie sa vycisti, dalsie otvorenie zacina odznova.
        dismissResult() {
            clearInterval(this.genTick);
            this.result = null;
            this.form = Object.assign({}, this.form, { klient_id: null, zadanie: "", cielova_cena: "", vzor_uuid: null });
            this.override = null;
            this.resetJudge();
            this.close();
        },
        fmtNum(n) {
            return Number(n).toLocaleString(NUM_LOCALE[this.$i18n.locale] || "sk-SK", { maximumFractionDigits: 2 });
        },
        fmtMoney(n) {
            const cele = Math.round(n * 100) % 100 === 0;
            const txt = Number(n).toLocaleString(NUM_LOCALE[this.$i18n.locale] || "sk-SK", {
                minimumFractionDigits: cele ? 0 : 2,
                maximumFractionDigits: 2,
            });
            // mena ulozenej ponuky (server), nie zobrazovacia mena uctu
            const mena = this.result && this.result.summary.mena ? this.result.summary.mena : this.currency.symbol;
            return txt + " " + mena;
        },
        // Zavretie je zmensenie: rozpisane udaje ostanu a generovanie bezi dalej.
        close() {
            if (this.saving) return;
            this.open = false;
            this.previewOpen = false;
            this.emitStatus();
        },
        // Zelena fajka az po prejdeni kroku — server vracia kroky, ktore pouzivatel
        // potvrdil tlacidlom Pokračovať (krok 1 staci vyplneny).
        isStepDone(s) {
            if (s === 4) return false;
            return this.completedSteps.indexOf(s) !== -1;
        },
        // Povinny je len krok 1. Kroky 2 a 3 su nepovinne, krok 3 ma defaulty.
        canReach(s) {
            return s === 1 || this.isStepDone(1);
        },
        stepClass(s) {
            if (this.step === s) return "on";
            if (this.isStepDone(s)) return this.canReach(s) ? "done" : "done locked";
            return this.canReach(s) ? "reachable" : "";
        },
        goTo(s) {
            if (s === this.step || this.saving) return;
            if (s > this.step && !this.canReach(s)) return;

            this.leaveStep(s).then((ok) => {
                if (!ok) return;
                this.error = "";
                this.step = s;
            });
        },
        // Pri odchode z kroku s profilom ho ulozime, aby sa zmeny nestratili.
        // Krok 2 sa uklada len pri ceste dopredu — tym ho pouzivatel potvrdil.
        leaveStep(target) {
            if (this.step === 1) {
                // Dozadu sa z kroku 1 ist neda, takze odchod je vzdy dopredu.
                if (!this.validateStep1()) return Promise.resolve(false);
                return this.saveStep(1);
            }
            if (this.step === 2) {
                this.picker = null;
                return target > 2 ? this.saveStep(2) : Promise.resolve(true);
            }
            if (this.step === 3) return this.saveStep(3);
            return Promise.resolve(true);
        },
        loadProfile(keepStep) {
            axios
                .get("/api/ai/profile")
                .then((res) => {
                    const d = res.data || {};
                    this.profile = Object.assign({}, d.profile || {});
                    this.openGroup = this.groupOf((this.profile.odbory || [])[0]);
                    // Nepovinne udaje zbalene, vyplnene hodnoty ukaze suhrn v hlavicke karty
                    this.optionalOpen = false;
                    this.completedSteps = d.completed_steps || [];
                    this.itemsStats = Object.assign({}, this.itemsStats, d.items_stats || {});
                    this.currency = d.currency || this.currency;
                    this.businessAreas = d.business_areas || [];

                    if (!keepStep) {
                        this.step = this.firstUnfinishedStep();
                    }
                })
                .catch(() => {
                    // Bez profilu sa da generovat — prompt zvladne aj prazdny profil.
                    if (!keepStep) this.step = 4;
                })
                .finally(() => {
                    this.booting = false;
                });
        },
        firstUnfinishedStep() {
            for (let s = 1; s <= 3; s++) {
                if (!this.isStepDone(s)) return s;
            }
            return 4;
        },
        loadKlienti() {
            // Vsetci klienti firmy, nie len ti z existujucich ponuk — novy pouzivatel
            // ziadne ponuky nema a zoznam by ostal prazdny.
            axios
                .get("/api/klients")
                .then((res) => {
                    this.klienti = Array.isArray(res.data) ? res.data : [];
                })
                .catch(() => {
                    this.klienti = [];
                })
                .finally(() => {
                    this.klientiLoaded = true;
                });
        },
        saveStep(step) {
            const payload = Object.assign({ step: step }, this.stepPayload(step));

            this.saving = true;
            this.error = "";

            return axios
                .post("/api/ai/profile", payload)
                .then((res) => {
                    this.completedSteps = res.data.completed_steps || this.completedSteps;
                    this.profile = Object.assign({}, this.profile, res.data.profile || {});
                    EventBus.$emit("ai-profile-saved");
                    return true;
                })
                .catch(() => {
                    this.error = this.$t("ai_offer.step_save_error");
                    return false;
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        stepPayload(step) {
            const p = this.profile;

            if (step === 2) return {};

            if (step === 1) {
                return {
                    typicke_sluzby: p.typicke_sluzby,
                    odbory: p.odbory,
                    odbor_iny: this.hasArea("ine") ? p.odbor_iny : null,
                    co_nerobime: p.co_nerobime,
                    region: p.region,
                    velkost_firmy: p.velkost_firmy,
                    hodinova_sadzba: p.hodinova_sadzba,
                    marza: p.marza,
                    prevazujuca_jednotka: p.prevazujuca_jednotka,
                    vlastne_pravidla: p.vlastne_pravidla,
                };
            }

            return {
                styl: p.styl,
                hladina: p.hladina,
                ton: p.ton,
                oslovenie: p.oslovenie,
                platnost_dni: p.platnost_dni,
                dodanie_text: p.dodanie_text,
                zaverecny_text: p.zaverecny_text,
            };
        },
        onPrimary() {
            if (this.saving) return;

            if (this.step === 4) {
                this.submit();
                return;
            }

            this.leaveStep(this.step + 1).then((ok) => {
                if (ok) this.step += 1;
            });
        },
        toggleOverride() {
            if (this.override) {
                this.override = null;
                return;
            }

            this.override = {
                styl: this.profile.styl,
                hladina: this.profile.hladina,
                ton: this.profile.ton,
                oslovenie: this.profile.oslovenie,
            };
        },
        submit() {
            const errors = {};
            const cena = String(this.form.cielova_cena === null ? "" : this.form.cielova_cena).trim();

            if (this.newClient) {
                errors.klient_id = this.$t("ai_offer.client_save_first");
            } else if (!this.form.klient_id) {
                errors.klient_id = this.$t("ai_offer.modal_no_client");
            }
            if (String(this.form.zadanie || "").trim().length < 10) {
                errors.zadanie = this.$t("ai_offer.modal_short_task");
            }
            if (cena !== "" && !(Number(cena) >= 1)) {
                errors.cielova_cena = this.$t("ai_offer.field_budget_invalid");
            }

            this.fieldErrors = errors;
            if (Object.keys(errors).length) return;

            this.error = "";
            this.loading = true;
            this.previewOpen = false;
            this.genStage = 0;
            this.genStart = Date.now();
            this.genNow = this.genStart;
            this.genStageAt = [this.genStart];
            clearInterval(this.genTimer);
            this.genTimer = setInterval(() => {
                if (this.genStage < 3) {
                    this.genStage += 1;
                    this.genStageAt.push(Date.now());
                }
            }, GEN_STEP_MS);
            this.startTick();
            this.emitStatus();

            const payload = Object.assign({}, this.form, this.override || {});
            if (!payload.vzor_uuid) delete payload.vzor_uuid;
            if (this.judge.available && this.judgeItems.length) {
                payload.polozky_ids = this.judgeItems.map((p) => p.id);
            }
            if (cena === "") {
                delete payload.cielova_cena;
            } else {
                payload.cielova_cena = Number(cena);
            }

            axios
                .post("/api/ai/offer/generate", payload)
                .then((res) => {
                    clearInterval(this.genTimer);
                    // Do editora sa neprechadza samo: okno ukaze hotovu ponuku s tlacidlami,
                    // zmensene okno — asistent ukaze bublinu.
                    const now = Date.now();
                    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
                    this.loading = false;
                    this.result = {
                        editUrl: res.data.edit_url,
                        viewUrl: res.data.view_url,
                        summary: res.data.summary || { nazov: "", uvod: "", klient: "", polozky: [], spolu: 0, mena: "" },
                        at: now,
                        secs: Math.max(1, Math.round((now - this.genStart) / 1000)),
                    };
                    // Vypisanie ma zmysel len ked sa pouzivatel pozera; inak je ponuka hned cela.
                    this.revealAt = this.open && !reduce ? now : now - 60000;
                    this.genNow = now;
                    this.startTick();
                    this.emitStatus();
                })
                .catch((err) => {
                    clearInterval(this.genTimer);
                    clearInterval(this.genTick);
                    this.loading = false;
                    // Chybu treba ukazat, aj ked bolo okno zmensene.
                    this.open = true;
                    this.emitStatus();
                    const data = (err.response && err.response.data) || {};

                    this.error = data.error || this.$t("ai_offer.modal_error");

                    // Upsell vie otvorit len OffersTable. Na inych strankach (napr.
                    // nastavenia uctu) nikto nepocuva, preto chybu nechavame v modale.
                    if (data.upgrade_required) {
                        EventBus.$emit("open-paid-modal");
                    }
                });
        },
        formatCena(value) {
            if (value === null || value === undefined) return "—";

            const cislo = Number(value);
            const desatinne = this.currency.code === "CZK" ? 0 : 2;
            const text = cislo.toLocaleString("sk-SK", {
                minimumFractionDigits: desatinne,
                maximumFractionDigits: desatinne,
            });

            return text + " " + this.currency.symbol;
        },
        resetJudge() {
            clearTimeout(this.judgeTimer);
            this.judgeSeq += 1;
            this.judge = prazdnyPosudok();
            this.klientTouched = false;
            this.klientSuggested = false;
            this.pocetTouched = false;
            this.pocetSuggested = false;
            this.vzorTouched = false;
            this.excludedItems = [];
        },
        loadTemplates() {
            this.templatesLoaded = false;
            axios
                .get("/api/ai/offer/templates")
                .then((res) => {
                    this.templates = (res.data && res.data.offers) || [];
                })
                .catch(() => {
                    this.templates = [];
                })
                .finally(() => {
                    this.templatesLoaded = true;
                });
        },
        onTaskInput() {
            this.clearError("zadanie");
            clearTimeout(this.judgeTimer);

            if (String(this.form.zadanie || "").trim().length < JUDGE_MIN_LENGTH) {
                this.judgeSeq += 1;
                this.judge = Object.assign(prazdnyPosudok(), { klient: this.judge.klient });
                return;
            }

            this.judgeTimer = setTimeout(this.runJudge, JUDGE_DEBOUNCE);
        },
        runJudge() {
            const seq = ++this.judgeSeq;
            this.judge.reading = true;

            axios
                .post("/api/ai/judge", { zadanie: this.form.zadanie })
                .then((res) => {
                    // Medzitym pouzivatel pisal dalej — starsiu odpoved zahodime.
                    if (seq === this.judgeSeq) this.applyJudge(res.data || {});
                })
                .catch(() => {
                    if (seq === this.judgeSeq) this.judge = prazdnyPosudok();
                })
                .finally(() => {
                    if (seq === this.judgeSeq) this.judge.reading = false;
                });
        },
        applyJudge(d) {
            if (!d.dostupne) {
                this.judge = prazdnyPosudok();
                return;
            }

            this.judge = {
                available: true,
                reading: false,
                chyba: d.chyba || [],
                polozky: d.polozky || [],
                klient: d.klient || null,
                vzor: d.vzor || null,
                rozsah: d.rozsah || null,
                styl: d.styl || null,
            };

            // Klienta predvyplnime, len kym ho pouzivatel nevybral sam.
            const id = d.klient && d.klient.id;
            const known = id && this.klienti.some((k) => k.id === id);
            if (!this.klientTouched && !this.newClient && known && (!this.form.klient_id || this.klientSuggested)) {
                this.form.klient_id = id;
                this.klientSuggested = true;
                this.clearError("klient_id");
            }

            const pocet = d.rozsah && ROZSAH_NA_POCET[d.rozsah.hodnota];
            if (!this.pocetTouched && pocet) {
                this.form.pocet_poloziek = pocet;
                this.pocetSuggested = true;
            }
        },
        onKlientPicked() {
            this.klientTouched = true;
            this.klientSuggested = false;
            this.clearError("klient_id");
        },
        pickPocet(value) {
            this.form.pocet_poloziek = value;
            this.pocetTouched = true;
            this.pocetSuggested = false;
        },
        useSuggestedVzor() {
            if (!this.judge.vzor) return;
            this.form.vzor_uuid = this.judge.vzor.uuid;
            this.vzorTouched = true;
        },
        useSuggestedStyle() {
            const st = this.styleSuggestion;
            if (!st) return;
            if (!this.override) this.toggleOverride();
            this.$set(this.override, "styl", st.value);
        },
        excludeItem(id) {
            this.excludedItems.push(id);
        },
        appendMissing(udaj) {
            const text = String(this.form.zadanie || "").replace(/\s+$/, "");
            const oddel = text === "" ? "" : /[.!?]$/.test(text) ? " " : ". ";
            this.form.zadanie = text + oddel + this.$t("ai_offer.judge_append_" + udaj);
            this.judge.chyba = this.judge.chyba.filter((c) => c !== udaj);

            this.$nextTick(() => {
                const el = this.$refs.task;
                if (!el) return;
                el.focus();
                el.setSelectionRange(el.value.length, el.value.length);
            });
        },
        areasOfGroup(id) {
            return this.businessAreas.filter((slug) => (slug === "ine" ? "ine" : PH_SKUPINY[slug] || "ine") === id);
        },
        groupOf(slug) {
            if (!slug) return null;
            return slug === "ine" ? "ine" : PH_SKUPINY[slug] || "ine";
        },
        pickGroup(g) {
            if (g.slugs.length === 1) {
                this.toggleArea(g.slugs[0]);
                this.openGroup = null;
                return;
            }
            this.openGroup = this.openGroup === g.id ? null : g.id;
        },
        ph(field) {
            return this.$t("ai_offer.ph." + this.phGroup + "." + field);
        },
        openPicker() {
            this.importMessage = "";
            this.error = "";
            this.picker = { loading: true, items: [], selected: {}, query: "" };

            axios
                .get("/api/ai/items/importable", { params: { list: 1 } })
                .then((res) => {
                    if (!this.picker) return;
                    const items = (res.data && res.data.items) || [];
                    // Nic nie je predvolene — pouzivatel si vyberie, co do cennika patri.
                    this.picker.items = items;
                    this.picker.loading = false;
                })
                .catch(() => {
                    this.picker = null;
                    this.error = this.$t("ai_offer.items_import_error");
                });
        },
        closePicker() {
            this.picker = null;
        },
        pickAll(on) {
            if (!this.picker) return;
            const selected = Object.assign({}, this.picker.selected);
            this.pickerFiltered.forEach((it) => {
                selected[it.uuid] = on;
            });
            this.picker.selected = selected;
        },
        importPicked() {
            if (this.importing || !this.picker || this.pickedCount === 0) return;

            const uuids = Object.keys(this.picker.selected).filter((k) => this.picker.selected[k]);

            this.importing = true;
            this.importMessage = "";
            this.error = "";

            axios
                .post("/api/ai/items/import", { uuids: uuids })
                .then((res) => {
                    this.itemsStats = Object.assign({}, this.itemsStats, res.data.items_stats || {});
                    this.importMessage = this.$t("ai_offer.items_import_done", {
                        count: res.data.imported || 0,
                    });
                    this.picker = null;
                })
                .catch(() => {
                    this.error = this.$t("ai_offer.items_import_error");
                })
                .finally(() => {
                    this.importing = false;
                });
        },
        openNewClient() {
            this.clearError("klient_id");
            this.newClient = prazdnyKlient();
            this.lookup = { loading: false, message: "", ok: false, ico: "" };
        },
        closeNewClient() {
            this.newClient = null;
        },
        onIcoInput() {
            const ico = String(this.newClient.ico || "").replace(/\D/g, "");
            this.lookup.message = "";
            // Pri 8 cislach hladame hned — tak to funguje aj Foxentry na verejnej ponuke.
            if (ico.length === 8 && ico !== this.lookup.ico) {
                this.lookupIco();
            }
        },
        lookupIco() {
            if (!this.newClient || this.lookup.loading) return;

            const ico = String(this.newClient.ico || "").replace(/\D/g, "");
            if (ico.length !== 8) {
                this.lookup.ok = false;
                this.lookup.message = this.$t("ai_offer.client_lookup_invalid");
                return;
            }

            this.lookup.loading = true;
            this.lookup.message = "";
            this.lookup.ico = ico;

            axios
                .get("/api/company-lookup", { params: { ico: ico } })
                .then((res) => {
                    const d = res.data || {};
                    if (!this.newClient) return;

                    if (!d.found) {
                        this.lookup.ok = false;
                        this.lookup.message =
                            d.error === "unreachable"
                                ? this.$t("ai_offer.client_lookup_error")
                                : this.$t("ai_offer.client_lookup_not_found");
                        return;
                    }

                    const k = this.newClient;
                    k.ico = d.ico || ico;
                    k.spolocnost = d.nazov || "";
                    // Prepiseme len prazdne polia — co uz pouzivatel napisal, nechame.
                    if (!k.nazov) k.nazov = d.nazov || "";
                    if (!k.ulica) k.ulica = d.ulica || "";
                    if (!k.psc) k.psc = d.psc || "";
                    if (!k.mesto) k.mesto = d.mesto || "";
                    if (!k.dic && d.dic) k.dic = d.dic;
                    k.error = "";

                    this.lookup.ok = true;
                    this.lookup.message = this.$t("ai_offer.client_lookup_found");
                })
                .catch(() => {
                    this.lookup.ok = false;
                    this.lookup.message = this.$t("ai_offer.client_lookup_error");
                })
                .finally(() => {
                    this.lookup.loading = false;
                });
        },
        saveNewClient() {
            const k = this.newClient;
            if (!k || k.saving) return;
            this.clearError("klient_id");

            const nazov = String(k.nazov || "").trim();
            if (!nazov) {
                k.error = this.$t("ai_offer.client_name_required");
                return;
            }
            // Rovnaka kontrola ako KlientCreate.vue — dvaja klienti s rovnakym nazvom sa pletu.
            const existuje = this.klienti.some((x) => String(x.nazov || "").trim() === nazov);
            if (existuje) {
                k.error = this.$t("ai_offer.client_name_exists");
                return;
            }

            k.saving = true;

            // /api/klients cita vsetky kluce klienta — chybajuci kluc by na serveri spadol.
            const klient = {
                nazov: nazov,
                posledny_kontakt: "",
                pripomenut_email: "",
                osoba: k.osoba,
                email: k.email,
                cislo: "",
                odvetie: "",
                poznamky: "",
                spolocnost: k.spolocnost || "",
                ico: String(k.ico || "").replace(/\D/g, ""),
                dic: k.dic,
                icdph: k.icdph,
                ulica: k.ulica,
                psc: k.psc,
                mesto: k.mesto,
                zdroj_kontaktu_id: null,
            };

            axios
                .post("/api/klients", { klient: klient, isEdit: false })
                .then((res) => {
                    const novy = res.data || {};
                    if (!novy.id) throw new Error("missing id");

                    this.klienti = this.klienti
                        .concat([novy])
                        .sort((a, b) => String(a.nazov).localeCompare(String(b.nazov), "sk"));
                    this.form.klient_id = novy.id;
                    this.newClient = null;
                    this.clearError("klient_id");
                })
                .catch(() => {
                    if (this.newClient) {
                        this.newClient.saving = false;
                        this.newClient.error = this.$t("ai_offer.client_save_error");
                    }
                });
        },
        hasArea(slug) {
            return (this.profile.odbory || []).indexOf(slug) !== -1;
        },
        toggleArea(slug) {
            const current = (this.profile.odbory || []).slice();
            const at = current.indexOf(slug);

            if (at !== -1) {
                current.splice(at, 1);
            } else {
                if (current.length >= 3) return;
                current.push(slug);
            }

            this.$set(this.profile, "odbory", current);
            this.clearError("odbory");

            if (slug === "ine" && at === -1) {
                this.$nextTick(() => {
                    if (this.$refs.odborIny) this.$refs.odborIny.focus();
                });
            } else if (slug === "ine") {
                this.clearError("odbor_iny");
            }
        },
        clearError(key) {
            if (this.fieldErrors[key]) this.$delete(this.fieldErrors, key);
        },
        validateStep1() {
            const errors = {};

            if (!(this.profile.odbory || []).length) {
                errors.odbory = this.$t("ai_offer.field_areas_required");
            }
            if (!String(this.profile.typicke_sluzby || "").trim()) {
                errors.typicke_sluzby = this.$t("ai_offer.field_services_required");
            }
            if (this.hasArea("ine") && !String(this.profile.odbor_iny || "").trim()) {
                errors.odbor_iny = this.$t("ai_offer.field_other_area_required");
            }

            this.fieldErrors = errors;

            return Object.keys(errors).length === 0;
        },
    },
};
</script>

<style lang="scss">
html.ai-scroll-lock,
html.ai-scroll-lock body {
    overflow: hidden;
}
</style>

<style lang="scss" scoped>
.ai-shell {
    position: fixed;
    inset: 0;
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 32px 24px;
}
.ai-scrim {
    position: absolute;
    inset: 0;
    border: 0;
    padding: 0;
    cursor: default;
    background: rgba(20, 22, 42, 0.62);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    animation: ai-fade 0.25s ease-out;
}
.ai-modal {
    position: relative;
    width: 1040px;
    max-width: 100%;
    // pevna vyska pre vsetky kroky (okno neskace), co sa nezmesti scrolluje v tele;
    // hore a dole vzdy ostane medzera
    height: min(860px, calc(100vh - 64px));
    background: #fff;
    border-radius: 22px;
    display: flex;
    overflow: hidden;
    box-shadow: 0 34px 90px rgba(15, 17, 40, 0.45);
    font-family: Roboto, "Montserrat", sans-serif;
    transform-origin: center;
    animation: ai-open 0.42s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.ai-side-preview {
    position: relative;
    flex: none;
    width: 420px;
    height: min(860px, calc(100vh - 64px));
    display: flex;
    > * {
        flex: 1;
    }
}
.ai-peek-enter-active,
.ai-peek-leave-active {
    transition: opacity 0.3s, transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.ai-peek-enter,
.ai-peek-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

/* lista krokov vlavo */
.ai-rail {
    width: 236px;
    flex: none;
    background: #212541;
    padding: 22px 14px 18px;
    display: flex;
    flex-direction: column;
}
.ai-rail-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 10px 22px;
    .logo {
        width: 34px;
        height: 34px;
        border-radius: 10px;
        background: #f3385a;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
    }
    b {
        display: block;
        font: 700 15px Montserrat, sans-serif;
        color: #fff;
    }
    small {
        display: block;
        font: 400 12px Roboto, sans-serif;
        color: rgba(255, 255, 255, 0.72);
    }
}
.ai-steps {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.ai-step {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    width: 100%;
    padding: 11px 12px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    text-align: left;
    font-family: inherit;
    opacity: 0.55;
    cursor: default;
    transition: background 0.2s, opacity 0.2s;
    b {
        flex: none;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: 1.5px solid rgba(255, 255, 255, 0.35);
        color: rgba(255, 255, 255, 0.85);
        display: flex;
        align-items: center;
        justify-content: center;
        font: 700 12.5px Montserrat, sans-serif;
        transition: all 0.25s;
    }
    .t {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding-top: 2px;
        span {
            font: 600 13.5px Montserrat, sans-serif;
            color: #fff;
        }
        small {
            font: 400 12px Roboto, sans-serif;
            color: rgba(255, 255, 255, 0.7);
        }
    }
    &.reachable,
    &.done,
    &.on {
        opacity: 1;
    }
    &.reachable,
    &.done:not(.locked) {
        cursor: pointer;
        &:hover {
            background: rgba(255, 255, 255, 0.05);
        }
    }
    &.on {
        background: rgba(255, 255, 255, 0.09);
        b {
            background: #f3385a;
            border-color: #f3385a;
            color: #fff;
            box-shadow: 0 0 0 5px rgba(243, 56, 90, 0.22);
        }
    }
    &.done b {
        background: #2ed8b6;
        border-color: #2ed8b6;
        color: #212541;
        animation: ai-pop 0.35s ease-out;
    }
    &:disabled {
        cursor: default;
    }
}

.ai-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}
.ai-modal-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-shrink: 0;
    padding: 22px 28px 18px;
    background: #f8fafc;
    border-bottom: 1px solid #e3e8ee;
    .kicker {
        font: 700 11.5px Roboto, sans-serif;
        color: #c81f45;
        letter-spacing: 0.08em;
        text-transform: uppercase;
    }
    h2 {
        margin: 6px 0 0;
        font: 700 22px Montserrat, sans-serif;
        color: #212541;
    }
    .lead {
        margin: 6px 0 0;
        font: 400 13.5px/1.5 Roboto, sans-serif;
        color: #5b6275;
        max-width: 560px;
    }
}
.ai-head-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: none;
}
.ai-tog {
    box-sizing: border-box;
    height: 38px;
    padding: 0 14px;
    margin: 0;
    border-radius: 10px;
    border: 1px solid #d5dce4;
    line-height: 1;
    background: #fff;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font: 600 13px Roboto, sans-serif;
    color: #374051;
    cursor: pointer;
    transition: all 0.18s;
    &:hover {
        border-color: #212541;
        color: #212541;
    }
    &.on {
        background: #212541;
        border-color: #212541;
        color: #fff;
    }
}
.ai-modal-head .close {
    // Bootstrap .close dava float, opacity .5 a text-shadow — vypiname, inak krizik
    // sedi nizsie a je blednuty
    float: none;
    opacity: 1;
    text-shadow: none;
    margin: 0;
    padding: 0;
    line-height: 1;
    box-sizing: border-box;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    border: 1px solid #d5dce4;
    background: #fff;
    color: #4a5164;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
        background: #f1f4f7;
        opacity: 1;
    }
}
.ai-steps-mobile {
    display: none;
}
.ai-dots {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-left: auto;
    i {
        width: 18px;
        height: 4px;
        border-radius: 2px;
        background: #dde3e8;
        &.done {
            background: #2ed8b6;
        }
        &.on {
            background: #f3385a;
        }
    }
}
.ai-modal-body,
.ai-modal .picker-list {
    overscroll-behavior: contain;
    scrollbar-width: thin;
    scrollbar-color: #cdd4de transparent;
    &::-webkit-scrollbar {
        width: 10px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: #cdd4de;
        border-radius: 10px;
        border: 3px solid transparent;
        background-clip: padding-box;
    }
    &::-webkit-scrollbar-thumb:hover {
        background-color: #aab3c0;
    }
}
.ai-modal-body {
    padding: 24px 28px;
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 0;
    animation: ai-slide 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
    &.inline-preview {
        display: flex;
        background: #f4f6f9;
        > * {
            flex: 1;
        }
    }
}
.ai-modal-loading {
    padding: 48px 24px;
    text-align: center;
    flex: 1;
    .spinner {
        width: 34px;
        height: 34px;
        margin: 0 auto;
        border-radius: 50%;
        border: 3px solid #ecf1f6;
        border-top-color: #f3385a;
        animation: ai-spin 0.8s linear infinite;
    }
}

/* generovanie: priebeh vlavo, papier ponuky vpravo */
.ai-modal-head {
    position: relative;
}
.ai-head-bar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 3px;
    i {
        display: block;
        height: 100%;
        background: #2ed8b6;
        transition: width 0.3s linear;
    }
}
.ai-modal-body.ai-gen {
    display: flex;
    padding: 0;
    background: #f4f6f9;
}
.ai-gen-side {
    width: 280px;
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 24px 22px 22px 28px;
    background: #fff;
    border-right: 1px solid #e3e8ee;
}
.ai-gen-pct {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 10px;
    span {
        font: 700 13px Montserrat, sans-serif;
        color: #212541;
    }
    b {
        font: 800 24px Montserrat, sans-serif;
        color: #212541;
        font-variant-numeric: tabular-nums;
    }
}
.ai-gen-line {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px;
    margin: 0 -10px;
    border-radius: 12px;
    transition: background 0.3s;
    .mark {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        flex: none;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px dashed #c3cad3;
    }
    .txt {
        flex: 1;
        min-width: 0;
    }
    // nie .row — Bootstrap ma .row s flex-wrap a zapornymi okrajmi
    .ln {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        > span {
            flex: 1;
            min-width: 0;
            font: 500 13.5px/20px Roboto, sans-serif;
            color: #8a90a0;
            transition: color 0.3s;
        }
        small {
            flex: none;
            min-width: 30px;
            text-align: right;
            font: 500 12px/20px Roboto, sans-serif;
            color: #8a90a0;
            font-variant-numeric: tabular-nums;
        }
    }
    .sub {
        display: block;
        height: 3px;
        margin-top: 8px;
        border-radius: 2px;
        background: #e8ecf1;
        overflow: hidden;
        i {
            display: block;
            height: 100%;
            background: #f3385a;
            transition: width 0.2s linear;
        }
    }
    &.run {
        background: #f4f6f9;
        .ln > span {
            color: #212541;
        }
        .mark {
            border: 2.5px solid #e3e8ee;
            border-top-color: #f3385a;
            animation: ai-spin 0.8s linear infinite;
        }
    }
    &.done {
        .ln > span {
            color: #374051;
        }
        .mark {
            border: 0;
            background: #2ed8b6;
            color: #212541;
            animation: ai-pop 0.3s ease-out;
        }
    }
}
.ai-gen-tip {
    margin-top: auto;
    background: #f4f6f9;
    border-radius: 12px;
    padding: 12px 14px;
    font: 400 12.5px/1.5 Roboto, sans-serif;
    color: #374051;
}
.ai-gen-sum {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px;
    border: 1px solid #bfeedd;
    border-radius: 14px;
    background: #effbf6;
    font: 400 12.5px/1.5 Roboto, sans-serif;
    color: #24584a;
    b {
        font: 700 13.5px Montserrat, sans-serif;
        color: #0b6b55;
    }
}
.ai-gen-stage {
    flex: 1;
    min-width: 0;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 24px 34px;
    overflow-y: auto;
}
.ai-sheet {
    position: relative;
    width: 100%;
    max-width: 480px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 28px 30px;
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(20, 24, 50, 0.08), 0 18px 40px rgba(20, 24, 50, 0.12);
    overflow: hidden;
    animation: ai-sheet-in 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) both;
    .sk {
        display: block;
        height: 11px;
        border-radius: 6px;
        background: linear-gradient(90deg, #eef1f5 0, #e2e7ee 40px, #eef1f5 80px) #eef1f5;
        background-size: 480px 100%;
        animation: ai-shimmer 1.3s linear infinite;
    }
    .caret {
        display: inline-block;
        width: 2px;
        height: 1em;
        margin-left: 2px;
        vertical-align: -2px;
        background: #f3385a;
        animation: ai-caret 0.8s steps(1) infinite;
    }
    .stamp {
        position: absolute;
        top: 22px;
        right: 22px;
        padding: 6px 12px;
        border: 2px solid #13b98c;
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.92);
        color: #0b6b55;
        font: 800 12px Montserrat, sans-serif;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        animation: ai-stamp 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) both;
    }
}
.sheet-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    .logo {
        height: 26px;
        padding: 0 12px;
        border-radius: 6px;
        background: #eef0fa;
        color: #2f3a86;
        font: 700 11px Montserrat, sans-serif;
        text-transform: uppercase;
        display: flex;
        align-items: center;
    }
    .to {
        text-align: right;
        font: 400 11.5px/1.5 Roboto, sans-serif;
        color: #5b6275;
        b {
            display: block;
            color: #212541;
        }
    }
}
.ai-gen.done .sheet-top .to {
    visibility: hidden;
}
.sheet-title {
    min-height: 24px;
    font: 700 18px/1.3 Montserrat, sans-serif;
    color: #212541;
}
.sheet-intro {
    min-height: 52px;
    font: 400 12.5px/1.6 Roboto, sans-serif;
    color: #374051;
    .sk + .sk {
        margin-top: 8px;
    }
}
.sheet-head {
    display: flex;
    justify-content: space-between;
    padding-bottom: 8px;
    border-bottom: 1.5px solid #212541;
    font: 700 10.5px Roboto, sans-serif;
    color: #5b6275;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}
.sheet-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    min-height: 40px;
    margin: 0 -8px;
    padding: 0 8px;
    border-bottom: 1px solid #eef1f5;
    border-radius: 6px;
    transition: background 0.35s;
    &.hot {
        background: #eefaf6;
    }
    .n {
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .nm {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font: 500 13px Roboto, sans-serif;
        color: #212541;
    }
    small {
        flex: none;
        font: 400 12px Roboto, sans-serif;
        color: #8a90a0;
        animation: ai-fade 0.3s ease-out;
    }
    small.est {
        padding: 2px 7px;
        border-radius: 999px;
        background: #fdf3e2;
        color: #8a5a12;
        font: 600 10.5px Roboto, sans-serif;
    }
    .p {
        flex: none;
        min-width: 84px;
        display: flex;
        justify-content: flex-end;
        font: 600 13px Roboto, sans-serif;
        color: #212541;
        font-variant-numeric: tabular-nums;
    }
}
.sheet-vat {
    display: flex;
    justify-content: space-between;
    margin-top: -12px;
    font: 500 12.5px Roboto, sans-serif;
    color: #5b6275;
    b {
        font-weight: 600;
        color: #374051;
        font-variant-numeric: tabular-nums;
    }
}
.sheet-rows {
    margin-top: -16px;
}
.sheet-more {
    padding-top: 8px;
    font: 500 12px Roboto, sans-serif;
    color: #5b6275;
}
.sheet-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: -4px -8px 0;
    padding: 8px;
    border-radius: 8px;
    span {
        font: 700 13px Montserrat, sans-serif;
        color: #212541;
    }
    b {
        font: 800 20px Montserrat, sans-serif;
        color: #212541;
        font-variant-numeric: tabular-nums;
    }
    &.flash {
        animation: ai-flash 1.2s ease-out;
    }
}
.ai-foot-wait {
    visibility: hidden;
}
.ai-btn1,
.ai-btn2 {
    box-sizing: border-box;
    height: 46px;
    padding: 0 20px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font: 700 13.5px Montserrat, sans-serif;
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;
    transition: border-color 0.18s, background 0.18s;
    &:hover,
    &:focus {
        text-decoration: none;
    }
}
.ai-btn1 {
    border: 0;
    background: #2ed8b6;
    color: #212541;
    animation: ai-glow 1.6s ease-in-out 3;
    &:hover {
        background: #27c7a7;
        color: #212541;
    }
}
.ai-btn2 {
    border: 1px solid #d5dce4;
    background: #fff;
    color: #212541;
    &:hover {
        border-color: #212541;
        color: #212541;
    }
}
@keyframes ai-sheet-in {
    from {
        opacity: 0;
        transform: translateY(24px) scale(0.98);
    }
    to {
        opacity: 1;
        transform: none;
    }
}
@keyframes ai-shimmer {
    from {
        background-position: -240px 0;
    }
    to {
        background-position: 240px 0;
    }
}
@keyframes ai-caret {
    50% {
        opacity: 0;
    }
}
@keyframes ai-stamp {
    0% {
        opacity: 0;
        transform: rotate(-14deg) scale(1.8);
    }
    60% {
        opacity: 1;
        transform: rotate(-8deg) scale(0.95);
    }
    100% {
        opacity: 1;
        transform: rotate(-8deg) scale(1);
    }
}
@keyframes ai-flash {
    0% {
        background: #d9f7ee;
    }
    100% {
        background: transparent;
    }
}
@keyframes ai-glow {
    0%,
    100% {
        box-shadow: 0 0 0 0 rgba(46, 216, 182, 0);
    }
    50% {
        box-shadow: 0 0 0 6px rgba(46, 216, 182, 0.2);
    }
}
@media (prefers-reduced-motion: reduce) {
    .ai-sheet,
    .ai-sheet .sk,
    .ai-sheet .caret,
    .ai-sheet .stamp,
    .ai-btn1 {
        animation: none;
    }
}
@media (max-width: 767px) {
    .ai-modal .ai-modal-body.ai-gen {
        flex-direction: column;
        overflow-y: auto;
    }
    .ai-modal .ai-gen-side {
        width: auto;
        border-right: 0;
        border-bottom: 1px solid #e3e8ee;
        padding: 16px 18px;
    }
    .ai-modal .ai-gen-stage {
        padding: 16px;
        overflow: visible;
    }
    .ai-modal .ai-sheet {
        padding: 20px 18px;
    }
}

.ai-modal-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 16px 28px;
    border-top: 1px solid #e3e8ee;
    background: #f8fafc;
    flex-shrink: 0;
    .back {
        cursor: pointer;
        color: #4a5164;
        font: 600 13.5px Montserrat, sans-serif;
        white-space: nowrap;
        &:hover {
            color: #212541;
        }
    }
    .foot-right {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
        justify-content: flex-end;
        min-width: 0;
    }
    .hint {
        font-size: 12px;
        color: #5b6275;
    }
    .submit {
        cursor: pointer;
        background: #2ed8b6;
        color: #212541;
        border-radius: 10px;
        height: 46px;
        padding: 0 22px;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font: 700 14px Montserrat, sans-serif;
        white-space: nowrap;
        transition: transform 0.15s, box-shadow 0.15s;
        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 10px 22px rgba(46, 216, 182, 0.38);
        }
        &.ai {
            background: #212541;
            color: #fff;
            svg {
                color: #2ed8b6;
            }
            &:hover {
                box-shadow: 0 10px 22px rgba(33, 37, 65, 0.3);
            }
        }
        &.busy {
            opacity: 0.6;
            pointer-events: none;
        }
    }
}
.ai-summary {
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    border: 1.5px dashed #c3cad3;
    background: #fff;
    border-radius: 999px;
    padding: 7px 14px;
    font: 600 12.5px Roboto, sans-serif;
    color: #374051;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
    &:hover {
        border-color: #212541;
        color: #212541;
    }
}

@keyframes ai-spin {
    to {
        transform: rotate(360deg);
    }
}
@keyframes ai-fade {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}
@keyframes ai-open {
    from {
        opacity: 0;
        transform: translateY(18px) scale(0.94);
    }
    to {
        opacity: 1;
        transform: none;
    }
}
@keyframes ai-slide {
    from {
        opacity: 0;
        transform: translateX(14px);
    }
    to {
        opacity: 1;
        transform: none;
    }
}
@keyframes ai-pop {
    0% {
        transform: scale(0.3);
        opacity: 0;
    }
    70% {
        transform: scale(1.15);
        opacity: 1;
    }
    100% {
        transform: scale(1);
    }
}
@keyframes ai-bar {
    from {
        transform: scaleX(0.02);
    }
    to {
        transform: scaleX(0.92);
    }
}

/* notebook: okno na stred, nahlad v okne */
@media (max-width: 1023px) {
    .ai-shell {
        align-items: center;
        justify-content: center;
        padding: 24px 16px;
    }
    .ai-modal {
        transform-origin: center;
    }
    .ai-rail {
        width: 200px;
    }
    .ai-modal .ai-grid-3 {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
/* mobil: cela obrazovka, kroky ako bodky hore */
@media (max-width: 767px) {
    .ai-shell {
        padding: 0;
    }
    .ai-modal {
        height: 100%;
        border-radius: 0;
    }
    .ai-rail {
        display: none;
    }
    .ai-modal-head {
        padding: 16px 18px 0;
        h2 {
            font-size: 19px;
        }
        .lead {
            display: none;
        }
    }
    .ai-tog span {
        display: none;
    }
    .ai-steps-mobile {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 18px 0;
    }
    .ai-step-m {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font: 600 13px Montserrat, sans-serif;
        color: #212541;
        b {
            width: 22px;
            height: 22px;
            border-radius: 50%;
            background: #f3385a;
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
        }
    }
    .ai-modal-body {
        padding: 16px 18px;
    }
    .ai-modal-foot {
        padding: 12px 18px 16px;
    }
    .ai-summary {
        display: none;
    }
    .ai-modal .ai-grid-2,
    .ai-modal .ai-grid-3 {
        grid-template-columns: minmax(0, 1fr);
    }
    .ai-modal .ai-area-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .ai-modal .ai-card-sec {
        padding: 14px;
    }
}
.error {
    color: #f3385a;
    font-size: 13.5px;
    margin: 14px 0 0;
}
/* pole: popis nad polom, potom pole, pod nim napoveda alebo chyba */
.ai-field {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
    input,
    textarea,
    select {
        width: 100%;
        box-sizing: border-box;
        border: 1px solid #d5dce4;
        border-radius: 10px;
        padding: 0 13px;
        font-size: 14px;
        color: #212541;
        background: #fff;
        height: 44px;
        font-family: inherit;
        transition: border-color 0.15s, box-shadow 0.15s;
        &:focus {
            outline: none;
            border-color: #212541;
            box-shadow: 0 0 0 3px rgba(33, 37, 65, 0.08);
        }
        &::placeholder {
            color: #8a90a0;
        }
    }
    textarea {
        height: 76px;
        padding: 11px 13px;
        resize: none;
        line-height: 1.5;
    }
    > label {
        order: -1;
        font: 600 12.5px Roboto, sans-serif;
        color: #374051;
        margin: 0;
    }
    &.plain > label {
        font-size: 12.5px;
    }
    .hint {
        display: block;
        font-size: 12px;
        color: #5b6275;
        line-height: 1.45;
        &.empty {
            color: #8a5a12;
        }
        a {
            color: #c81f45;
            text-decoration: underline;
        }
    }
    .field-error {
        display: block;
        font-size: 12px;
        color: #d01c43;
    }
    &.invalid {
        input,
        textarea,
        .with-icon {
            border-color: #f3385a;
        }
    }
}

/* krok 1-3: sekcie ako karty na sivom pozadi */
.ai-modal-body.s1,
.ai-modal-body.s2,
.ai-modal-body.s3 {
    background: #f4f6f9;
}
.ai-cards {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.ai-card-sec {
    background: #fff;
    border: 1px solid #e3e8ee;
    border-radius: 14px;
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    animation: ai-fade-up 0.35s ease-out both;
    &:nth-child(2) {
        animation-delay: 60ms;
    }
    &:nth-child(3) {
        animation-delay: 120ms;
    }
    .ai-field {
        margin-bottom: 0;
    }
    .chips {
        margin-bottom: 0;
    }
    &.invalid {
        border-color: #f3385a;
    }
}
.ai-card-h {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    .num {
        flex: none;
        width: 26px;
        height: 26px;
        border-radius: 8px;
        background: #eef0fa;
        color: #2f3a86;
        font: 700 12.5px Montserrat, sans-serif;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    b {
        display: block;
        font: 700 15px Montserrat, sans-serif;
        color: #212541;
    }
    small {
        display: block;
        font: 400 13px/1.45 Roboto, sans-serif;
        color: #5b6275;
        margin-top: 2px;
    }
}
.ai-card-h.ai-card-toggle {
    width: 100%;
    border: 0;
    padding: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
    > span:nth-child(2) {
        flex: 1;
    }
}
.ai-card-more {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    align-self: center;
    font: 700 12.5px Roboto, sans-serif;
    color: #c81f45;
    svg {
        transition: transform 0.25s;
    }
}
.ai-card-h small.ai-card-sum {
    color: #212541;
    font-weight: 500;
}
.ai-reveal {
    animation: ai-fade-up 0.25s ease-out;
}
.ai-modal .field-error {
    display: flex;
    align-items: center;
    gap: 6px;
    font: 500 12.5px/1.4 Roboto, sans-serif;
    color: #d01c43;
    &::before {
        content: "!";
        flex: none;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #fde7ec;
        color: #d01c43;
        font: 700 11px/16px Roboto, sans-serif;
        text-align: center;
    }
}
.ai-grid-2,
.ai-grid-3 {
    display: grid;
    gap: 14px;
    > .ai-field {
        margin-bottom: 0;
        min-width: 0;
    }
}
.ai-grid-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}
.ai-grid-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

/* odbory: dlazdice skupin s ikonou */
.ai-area-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}
.ai-area-tile {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 48px;
    padding: 6px 12px;
    border: 1.5px solid #e3e8ee;
    border-radius: 12px;
    background: #fff;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
    transition: border-color 0.18s, background 0.18s, color 0.18s;
    .ic {
        flex: none;
        width: 32px;
        height: 32px;
        border-radius: 9px;
        background: #f1f4f8;
        color: #212541;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.18s, color 0.18s;
    }
    .tx {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 1px;
    }
    b {
        font: 600 13px Montserrat, sans-serif;
        color: inherit;
        line-height: 1.25;
    }
    small {
        font: 600 11.5px Roboto, sans-serif;
        color: #127a64;
    }
    &:hover {
        border-color: #212541;
    }
    &.has {
        border-color: #2ed8b6;
        .ic {
            background: #eafbf6;
            color: #127a64;
        }
    }
    &.open {
        border-color: #212541;
        background: #212541;
        color: #fff;
        .ic {
            background: rgba(255, 255, 255, 0.14);
            color: #2ed8b6;
        }
        small {
            color: #2ed8b6;
        }
    }
}
.chips.areas {
    margin: -2px 0 0;
    padding: 12px;
    border-radius: 12px;
    background: #f8fafc;
    animation: ai-fade-up 0.25s ease-out;
    .chip.off {
        opacity: 0.45;
        cursor: not-allowed;
    }
}

/* krok 4: polia s ikonou v riadku */
.ai-step4 {
    display: flex;
    flex-direction: column;
    gap: 18px;
    > .ai-field,
    .ai-grid-2 > .ai-field {
        margin-bottom: 0;
    }
}
.with-icon {
    display: flex;
    align-items: center;
    gap: 10px;
    box-sizing: border-box;
    min-height: 44px;
    padding: 0 13px;
    border: 1px solid #d5dce4;
    border-radius: 10px;
    background: #fff;
    transition: border-color 0.15s, box-shadow 0.15s;
    &:focus-within {
        border-color: #212541;
        box-shadow: 0 0 0 3px rgba(33, 37, 65, 0.08);
    }
    .fi {
        flex: none;
        color: #6b7183;
        &.cur {
            font: 700 14px Roboto, sans-serif;
            width: 16px;
            text-align: center;
        }
    }
    input,
    textarea {
        flex: 1;
        min-width: 0;
        border: 0;
        padding: 0;
        height: 42px;
        box-shadow: none;
        background: transparent;
        &:focus {
            box-shadow: none;
        }
    }
    &.area {
        align-items: flex-start;
        padding: 12px 13px;
        .fi {
            margin-top: 3px;
        }
        textarea {
            height: 118px;
            padding: 0;
        }
    }
}
.ai-seg {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px;
    border-radius: 10px;
    background: #eef1f5;
    align-self: flex-start;
    .chip {
        border: 0;
        background: transparent;
        border-radius: 8px;
        padding: 8px 14px;
        font: 600 13px Roboto, sans-serif;
        color: #4a5164;
        cursor: pointer;
        &:hover {
            color: #212541;
            border: 0;
        }
        &.on {
            background: #fff;
            color: #212541;
            box-shadow: 0 2px 6px rgba(33, 37, 65, 0.12);
        }
    }
}
.ico-wrap {
    position: relative;
    display: block;
    input {
        padding-right: 90px;
    }
}
.preset-box {
    border: 1px dashed #dde3e8;
    border-radius: 10px;
    padding: 12px 14px;
    .t {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        font-size: 12.5px;
        color: #374051;
        flex-wrap: wrap;
        a {
            color: #f3385a;
            font-weight: 600;
            cursor: pointer;
            white-space: nowrap;
            &:hover {
                text-decoration: underline;
            }
        }
    }
    .preset-edit {
        margin-top: 12px;
        .chips {
            margin-bottom: 10px;
        }
    }
    .hint {
        display: block;
        font-size: 12px;
        color: #9598a7;
    }
}
.style-hint {
    font-size: 12px;
    color: #9598a7;
    line-height: 1.45;
    margin: -8px 0 16px;
}
.stat-row {
    display: flex;
    gap: 14px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}
.stat-box {
    flex: 1 1 160px;
    border: 1px solid #dde3e8;
    border-radius: 10px;
    padding: 14px 16px;
    .n {
        font-family: "Montserrat", Roboto, sans-serif;
        font-weight: 700;
        font-size: 26px;
        color: #212541;
        line-height: 1;
    }
    .l {
        font-size: 12px;
        color: #9598a7;
        margin-top: 6px;
    }
    &.warn {
        border-color: #fbd3da;
        background: #fff6f8;
        .n {
            color: #f3385a;
        }
    }
    &.ok {
        border-color: #c6f2e8;
        background: #f2fcf9;
        .n {
            color: #26b195;
        }
    }
}
.item-list {
    border: 1px solid #dde3e8;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 16px;
    > div {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        padding: 9px 14px;
        font-size: 12.5px;
        color: #374051;
        border-top: 1px solid #ecf1f6;
    }
    .head {
        border-top: 0;
        background: #f7f8f8;
        color: #9598a7;
        font-weight: 600;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }
    .price {
        white-space: nowrap;
        color: #212541;
        font-weight: 600;
    }
    .more {
        color: #9598a7;
    }
}
.ai-card {
    border: 1px solid #dde3e8;
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
    flex-wrap: wrap;
    background: #fff;
    &.dashed {
        border-style: dashed;
    }
    p {
        margin: 0;
        font-size: 13px;
        color: #374051;
        line-height: 1.45;
        flex: 1 1 220px;
        b {
            color: #212541;
        }
    }
}
.btn-dark {
    background: #212541;
    color: #fff;
    border-radius: 8px;
    padding: 10px 16px;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
    &:hover {
        background: #374051;
    }
    &.busy {
        opacity: 0.6;
        pointer-events: none;
    }
}
.btn-line {
    border: 1px solid #dde3e8;
    color: #212541;
    background: #fff;
    border-radius: 8px;
    padding: 10px 16px;
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
    text-decoration: none;
    &:hover {
        border-color: #212541;
        color: #212541;
        text-decoration: none;
    }
}
.import-msg {
    font-size: 13px;
    color: #26b195;
    font-weight: 600;
    margin: -4px 0 14px;
}
.ai-row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    > .ai-field {
        flex: 1 1 180px;
    }
}
.ai-section {
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #9598a7;
    font-weight: 700;
    margin: 6px 0 10px;
}
.chips {
    display: flex;
    gap: 7px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}
.ai-optional-head {
    margin: 22px 0 12px;
    padding-top: 16px;
    border-top: 1px solid #ecf1f6;
    b {
        display: block;
        font-family: "Montserrat", Roboto, sans-serif;
        font-size: 13.5px;
        font-weight: 700;
        color: #212541;
    }
    span {
        display: block;
        font-size: 12px;
        color: #9598a7;
        margin-top: 3px;
        line-height: 1.45;
    }
}
.empty-state {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    border: 1px solid #c6f2e8;
    background: #f2fcf9;
    border-radius: 10px;
    padding: 16px 18px;
    margin-bottom: 16px;
    .empty-icon {
        flex: none;
        width: 40px;
        height: 40px;
        border-radius: 10px;
        background: #fff;
        color: #26b195;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    b {
        display: block;
        font-size: 14px;
        color: #212541;
        margin-bottom: 4px;
    }
    p {
        margin: 0;
        font-size: 13px;
        line-height: 1.5;
        color: #374051;
    }
}
.picker {
    border: 1px solid #dde3e8;
    border-radius: 10px;
    margin-bottom: 14px;
    overflow: hidden;
    .picker-head {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 12px;
        flex-wrap: wrap;
        padding: 12px 14px;
        background: #f7f8f8;
        b {
            font-size: 13.5px;
            color: #212541;
        }
    }
    .picker-count {
        font-size: 12px;
        color: #9598a7;
    }
    .picker-loading {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 18px 14px;
        font-size: 13px;
        color: #9598a7;
    }
    .picker-tools {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 10px 14px;
        border-bottom: 1px solid #ecf1f6;
        flex-wrap: wrap;
        a {
            font-size: 12.5px;
            font-weight: 600;
            color: #f3385a;
            cursor: pointer;
            white-space: nowrap;
            &:hover {
                text-decoration: underline;
            }
        }
    }
    .picker-search {
        flex: 1 1 200px;
        border: 1px solid #dde3e8;
        border-radius: 8px;
        padding: 8px 11px;
        font-size: 13px;
        color: #212541;
        &:focus {
            outline: none;
            border-color: #212541;
        }
        &::placeholder {
            color: #bec3cf;
        }
    }
    .picker-list {
        max-height: 280px;
        overflow-y: auto;
    }
    .picker-row {
        position: relative;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 14px;
        margin: 0;
        border-top: 1px solid #f2f4f6;
        font-size: 12.5px;
        color: #374051;
        cursor: pointer;
        &:first-child {
            border-top: 0;
        }
        &:hover {
            background: #fafbfc;
        }
        // admin tema natívne checkboxy skryva — kreslime vlastny
        .native {
            position: absolute;
            opacity: 0;
            width: 1px;
            height: 1px;
            pointer-events: none;
        }
        .box {
            flex: none;
            width: 18px;
            height: 18px;
            border: 1.5px solid #c9d0d8;
            border-radius: 5px;
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background 0.12s, border-color 0.12s;
            svg {
                opacity: 0;
            }
            &.on {
                background: #2ed8b6;
                border-color: #2ed8b6;
                svg {
                    opacity: 1;
                }
            }
        }
        .native:focus-visible + .box {
            box-shadow: 0 0 0 3px rgba(46, 216, 182, 0.3);
        }
        .name {
            flex: 1;
            min-width: 0;
        }
        .price {
            white-space: nowrap;
            font-weight: 600;
            color: #212541;
        }
    }
    .picker-empty {
        padding: 14px;
        font-size: 13px;
        color: #9598a7;
    }
    .picker-foot {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 12px 14px;
        border-top: 1px solid #ecf1f6;
        background: #fbfcfd;
        .btn-line {
            cursor: pointer;
        }
    }
}
.btn-dark.disabled {
    opacity: 0.4;
    pointer-events: none;
}
.spinner.small {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #ecf1f6;
    border-top-color: #f3385a;
    animation: ai-spin 0.8s linear infinite;
}
.select-action {
    display: block;
    padding: 9px 10px;
    border-radius: 8px;
    font-size: 13.5px;
    font-weight: 600;
    color: #f3385a;
    cursor: pointer;
    &:hover {
        background: #fff6f8;
    }
}
.new-client-link {
    display: inline-block;
    margin-top: 6px;
    font-size: 12.5px;
    font-weight: 600;
    color: #f3385a;
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
}
.new-client {
    border: 1px solid #dde3e8;
    border-radius: 10px;
    padding: 14px 14px 4px;
    margin-bottom: 16px;
    background: #fbfcfd;
    .new-client-head {
        margin-bottom: 12px;
        b {
            display: block;
            font-size: 13.5px;
            color: #212541;
        }
        .hint {
            font-size: 12px;
            color: #9598a7;
        }
    }
    .ai-field.zip {
        flex: 0 1 110px;
    }
    .new-client-foot {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
        margin-bottom: 10px;
        .field-error {
            margin-right: auto;
            font-size: 12px;
            color: #f3385a;
        }
        .btn-line {
            cursor: pointer;
        }
    }
}
.ico-field {
    .lookup {
        position: absolute;
        right: 6px;
        top: 50%;
        transform: translateY(-50%);
        padding: 6px 10px;
        border-radius: 6px;
        background: #212541;
        color: #fff;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        &:hover {
            background: #374051;
        }
        &.busy {
            opacity: 0.6;
            pointer-events: none;
        }
    }
}
.ai-field .hint.ok {
    color: #26b195;
}
.field-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}
.ai-suggest {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    font-size: 12.5px;
    font-weight: 600;
    color: #4353a4;
    animation: ai-fade-up 0.3s ease-out;
    .spark {
        flex: none;
    }
    b {
        margin-left: 4px;
        color: #c81f45;
    }
    &.action {
        cursor: pointer;
        background: #eef0fa;
        border-radius: 8px;
        padding: 6px 10px;
        &:hover b {
            text-decoration: underline;
        }
    }
    &.inline {
        margin: 0 0 0 8px;
        text-transform: none;
        letter-spacing: 0;
        font-size: 11.5px;
    }
    &.style {
        margin: 0 0 10px;
        display: flex;
    }
}
.judge-strip {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 7px;
    min-height: 26px;
    font-size: 12.5px;
    animation: ai-fade-up 0.25s ease-out;
}
.judge-reading {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #5b6275;
}
.judge-dots {
    display: inline-flex;
    gap: 3px;
    i {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: #4353a4;
        animation: ai-dot 1s infinite;
        &:nth-child(2) {
            animation-delay: 0.15s;
        }
        &:nth-child(3) {
            animation-delay: 0.3s;
        }
    }
}
.judge-label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #4a5164;
    font-weight: 600;
    .spark {
        color: #4353a4;
    }
}
.judge-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #fdf3e2;
    color: #6b4410;
    border-radius: 10px;
    padding: 6px 11px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    b {
        color: #8a5a12;
    }
    &:hover {
        background: #fae8c8;
    }
}
.judge-ok {
    color: #127a64;
    font-weight: 600;
}
.judge-items {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin: -4px 0 14px;
    font-size: 12.5px;
    animation: ai-fade-up 0.3s ease-out;
}
.judge-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #f1f4f7;
    border-radius: 999px;
    padding: 3px 4px 3px 10px;
    color: #212541;
    button {
        border: 0;
        background: transparent;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        line-height: 1;
        font-size: 15px;
        color: #5b6275;
        cursor: pointer;
        &:hover {
            background: #e3e8ee;
            color: #212541;
        }
    }
}
.judge-more {
    color: #5b6275;
    font-weight: 600;
}
@keyframes ai-fade-up {
    from {
        opacity: 0;
        transform: translateY(4px);
    }
    to {
        opacity: 1;
        transform: none;
    }
}
@keyframes ai-dot {
    0%,
    80%,
    100% {
        opacity: 0.3;
        transform: translateY(0);
    }
    40% {
        opacity: 1;
        transform: translateY(-3px);
    }
}
.chip {
    border: 1px solid #dde3e8;
    border-radius: 999px;
    padding: 6px 13px;
    font-size: 12.5px;
    color: #374051;
    cursor: pointer;
    user-select: none;
    transition: border-color 0.15s, background 0.15s;
    &:hover {
        border-color: #212541;
    }
    &.on {
        border-color: #212541;
        background: #212541;
        color: #fff;
        font-weight: 600;
    }
}
</style>
